import React, { useContext, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useLocation, useNavigate } from 'react-router-dom';
import { RemoveCloneEnvironmentFromLocal, RemoveEnvironmentFromLocal, acmCertificateForPayload, flattenErrorDetails, formatErrors, validateYaml } from './helper';
import { Button } from 'antd';
import { AlertContext } from './alertContext';
import rightIcon from '../assets/images/rightIcon.svg';
import impIcon from '../assets/images/impIcon.svg';
import { yamlContent } from './yamlContent';
// import { useDeploymentFeatures } from '../containers/Dashboard/CustomHooks/useDeploymentFeatures';
import { AppContext } from './ContextProvider';
import useSentryContext from '../utils/sentryContext';

/** deployments.tags is usually [{ name, value }] from the form; blueprint/API may send a plain object map. */
const normalizeDeploymentTagsForPayload = (tags) => {
    if (!tags) return [];
    if (Array.isArray(tags)) return tags;
    if (typeof tags === "object") {
        return Object.entries(tags).map(([name, value]) => ({
            name: name != null ? String(name) : "",
            value: value != null ? String(value) : "",
        }));
    }
    return [];
};

/** Form stores selfManaged.labels as [{ name, value }]; API expects { key: value }. */
const buildSelfManagedLabelsPayload = (rawLabels) => {
    const labels = {};
    if (Array.isArray(rawLabels)) {
        rawLabels.forEach((item) => {
            if (item.name !== "" && item.value !== "") {
                labels[item.name] = item.value;
            }
        });
    } else if (rawLabels && typeof rawLabels === "object") {
        Object.assign(labels, rawLabels);
    }
    return labels;
};

const DeploymentContext = React.createContext();

const DeploymentProvider = ({ children }) => {

    let navigate = useNavigate();

    let initialDeploymentData = {
        envName: "",
        clusterName: "",
        clusterAccount: "",
        clusterIntegrationId: 0,
        clusterId: "",
        clusterDbId: "",
        clusterNamespace: "",
        clusterRegion: "",
        azList: [],
        ingressAlbEnabled: false,
        ingressNginxEnabled: false,
        projectName: "",
        projectId: "",
        description: "",
        enableIstio: false,
        provisioner: "",
        vpc_id: "",
        tags: [{
            name: "",
            value: ""
        }],
    };

    const initialProject = {
        id: "",
        name: "",
        title: "",
        created_at: new Date(),
        progress: "",
        selectedServiceId: "",
        tasks: [],
        columns: {
            'app': {
                id: 'app',
                board_id: 'board-1',
                task_orders: [],
                title: 'APPLICATIONS',
                editing: false,
            },
            'dataSource': {
                id: 'dataSource',
                board_id: 'board-1',
                task_orders: [],
                title: 'DATA SOURCES',
                editing: false,
            },
            // 'queues': {
            //     id: 'queues',
            //     board_id: 'board-2',
            //     task_orders: [],
            //     title: 'QUEUES',
            //     editing: false,
            // },
            // 'thirdParties': {
            //   id: 'thirdParties',
            //   board_id: 'board-1',
            //   task_orders: [],
            //   title: 'THIRD PARTIES',
            //   editing: false,
            // },
        },
    }

    let initialApplicationData = {
        basicDetails: {
            appName: "",
            serviceName: "",
            url: "",
            sourceType: "",
            imageRepo: "",
            imageTag: "", // this image tag is the input taken from the user when they add a container registry type env service where the service will get created.
            containerRegistryType: 1,
            appSource: {},
            branch: "",
            dockerFilePath: "Dockerfile",
            buildContext: "",
            containerRegistryId: {},
            enableAutoTrigger: false,
            architectureType: "amd64",
        },
        environments: [],
        volumes: [{
            volumeType: "",
            config: {
                configEnabled: false,
                configName: "",
                createConfig: "",
                createMountPath: "",
                configFileName: "",
            },
            storage: {
                createStorage: false,
                storageDes: {
                    storageName: "",
                    size: "",
                    type: "EBS", //Fixed to EBS for now
                    path: ""
                }
            },
            secrets: {
                secretsEnabled: false,
                secretsName: "",
                createSecrets: "",
                createSecretsMountPath: "",
                secretsFileName: "",
            }
        }],
        deployments: {
            deploymentOption: "visual",
            repositoryNameClone: null,
            imageTagClone: null, //this image tag is the tag created after the env is created.
            "Deployment": {
                yamlString: yamlContent["Deployment"],
            },
            "Job & CronJob": {
                yamlString: yamlContent["Job & CronJob"]
            },
            "StatefulSet": {
                yamlString: yamlContent["StatefulSet"]
            },
            yamlType: "Deployment",
            yamlData: [],
            port: "",
            customDomain: false,
            domainHostUrl: "",
            domainHostName: "",
            endpointType: "",
            serviceType: "",
            acmCertificate: "",
            nodeGroupName: "",
            nodeGroupType: "",
            multiselectNodeGroupList: "",
            listOfNodegroups: [],
            labels: [{ name: "", value: "" }],
            dns: "",
            hostedAccount: null,
            recordType: null,
            gatewayHttps: {
                gPath: "",
                gPort: "",
                https: false
            },
            loadBalancer: {
                tcp: "",
                udp: "",
                lHttps: ""
            },
            tags: [{
                name: "",
                value: ""
            }],
            resources: {
                cpu: {
                    requestCpu: "",
                    limitCpu: ""
                },
                memory: {
                    requestMem: "",
                    limitMem: ""
                }
            },
            serverPreference: "",

            healthCheckPath: "",
            instanceCapacityType: {
                spot: "",
                onDemand: "",
                //minimumReplica: "",
            },
            // serverPreference: {
            //     compute: "",
            //     serverMemory: "",
            //     gpu: ""
            // },
            taskSchedulers: {
                crons: false,
                queuesAndWorkers: false,
                entryPointCommandCrons: "",
                entryPointCommandQueues: ""
            },
            autoScaling: {
                enabled: false,
                autoScalingData: {
                    minimumReplica: "",
                    maximumReplica: "",
                    autoScalingParameter: {
                        cpuAuto: "",
                        memoryAuto: ""
                    }
                }
            }
        },
        source: "",
        serviceEnabled: true,
        serviceSelectedId: "",

        //description: "",


        dockerRepo: "",
        // envPlainVars: [],
        // envSecretVars: [],
        advancedSettings: {

        }
    };

    let initialMongoDataSourcesData = {
        serviceName: "",
        displayName: "",
        version: "",
        // type: "",
        type: "Self Managed",
        serviceSelectedId: "",
        enbleMultiAzs: false,

        serviceEnabled: true,
        selfManaged: {
            storageType: "EBS",//Fixed to EBS for now
            serviceSelf: true,
            dbType: "",
            username: "",
            password: "",
            autoGenPassword: true,
            enableMetrics: false,
            databaseName: "",
            storageSize: "",
            //enableLogs: "",
            //logRetention: "",
            //typeOfLogs: "",
            enableBackup: "",
            backupRetention: "",
            backupFrequency: "",
            backupDay: "",
            // backupTimingStart: "",
            // backupTimingEnd: "",
            backupInterval: "",
            backupTiming: "",
            enableCrossRegionBackup: false,
            crossRegionBackup: "",
            enableRestore: false,

            nodeGroupName: "",
            nodeGroupType: "",
            multiselectNodeGroupList: "",
            listOfNodegroups: [],
            requestCpuPrimary: "",
            limitCpuPrimary: "",
            requestCpuSecondary: "",
            limitCpuSecondary: "",
            requestMemPrimary: "",
            limitMemPrimary: "",
            requestMemSecondary: "",
            limitMemSecondary: "",
            tags: [{ name: "", value: "" }],
            labels: [{ name: "", value: "" }]
        },
        // cloudManaged: {
        //     serviceAws: false,
        //     instanceType: "",
        //     instanceFamily: "",
        //     storageSize: "",
        //     dbType: "",
        //     enablePublicAccess: false,
        //     username: "",
        //     autoGenPassword: true,
        //     password: "",
        //     deletionProtection: false,
        //     databaseName: "",
        //     enableLogs: false,
        //     enableGeneralLogs: false,
        //     enableSlowLogs: false,
        //     logRetention: "",
        //     typeOfLogs: "",
        //     enableBackup: false,
        //     backupRetention: "",
        //     backupTimingStart: "",
        //     backupTimingEnd: "",
        //     backupLocation: "",
        //     kmsEncryption: "",
        //     tlsEncryption: false,
        //     tags: [{ name: "", value: "" }],
        //     maintainanceWindowStart: "",
        //     maintainanceWindowEnd: ""
        // }
    }

    let initialDataSourcesData = {
        serviceName: "",
        displayName: "",
        version: "",
        // type: "",
        type: "Self Managed",
        serviceSelectedId: "",
        enbleMultiAzs: false,

        serviceEnabled: true,
        selfManaged: {
            storageType: "EBS", //Fixed to EBS for now
            serviceSelf: true,
            dbType: "",
            username: "",
            password: "",
            autoGenPassword: true,
            enableMetrics: false,
            databaseName: "",
            storageSize: "",
            masterStorageSize: "",
            replicaStorageSize: "",
            //enableLogs: "",
            //logRetention: "",
            //typeOfLogs: "",
            enableBackup: "",
            backupRetention: "",
            backupFrequency: "",
            backupDay: "",
            // backupTimingStart: "",
            // backupTimingEnd: "",
            backupInterval: "",
            backupTiming: "",
            enableCrossRegionBackup: false,
            crossRegionBackup: "",
            enableRestore: false,

            nodeGroupName: "",
            nodeGroupType: "",
            multiselectNodeGroupList: "",
            listOfNodegroups: [],
            requestCpuPrimary: "",
            limitCpuPrimary: "",
            requestCpuSecondary: "",
            limitCpuSecondary: "",
            requestMemPrimary: "",
            limitMemPrimary: "",
            requestMemSecondary: "",
            limitMemSecondary: "",
            tags: [{ name: "", value: "" }],
            labels: [{ name: "", value: "" }]
        },
        cloudManaged: {
            serviceAws: false,
            instanceFamily: "",
            instanceType: "",
            storageSize: "",
            subnetIds: {
                publicSubnets: [],
                privateSubnets: [],
            },
            dbType: "",
            enablePublicAccess: false,
            username: "",
            autoGenPassword: true,
            password: "",
            deletionProtection: false,
            databaseName: "",
            enableLogs: false,
            enableGeneralLogs: false,
            enableSlowLogs: false,
            logRetention: "",
            typeOfLogs: "",
            enableBackup: false,
            backupRetention: "",
            backupTimingStart: "",
            backupTimingEnd: "",
            backupLocation: "",
            backupRegion: "",
            kmsEncryption: "",
            tlsEncryption: false,
            tags: [{ name: "", value: "" }],
            labels: [{ name: "", value: "" }],
            maintenanceWindowStart: "",
            maintenanceWindowEnd: "",
            maintenanceWindowDay: "",

            nodeGroupName: "",
            nodeGroupType: "",
            multiselectNodeGroupList: "",
            listOfNodegroups: [],
        }
    };

    const initialRedisData = {
        serviceName: "",
        displayName: "",
        // type: "",
        type: "Self Managed",
        version: "",
        serviceSelectedId: "",
        serviceEnabled: true,
        cloudManaged: {
            instanceFamily: "",
            instanceType: "",
            noOfReplica: "",
            subnetIds: {
                publicSubnets: [],
                privateSubnets: [],
            },
            enableMultiAz: false,
            enableAutomaticFailover: false,
            username: "",
            autoGenPassword: true,
            password: "",
            enableBackup: false,
            backupTimingStart: "",
            backupTimingEnd: "",
            backupRetention: "",
            maintenanceWindowDay: "",
            maintenanceWindowStart: "",
            maintenanceWindowEnd: "",
            serviceCloud: false,
            enableMetrics: false,
            enableAutomaticFailover: false,
            enableRestEncryption: false,
            enableTransitEncryption: false,

            nodeGroupName: "",
            nodeGroupType: "",
            multiselectNodeGroupList: "",
            listOfNodegroups: [],
        },
        selfManaged: {
            storageType: "EBS",//Fixed to EBS for now
            serviceSelf: true,
            noOfReplica: "",//db type
            storageSize: "",
            username: "default",
            autoGenPassword: true,
            password: "",
            enableMetrics: false,
            authEnabled: false,

            nodeGroupName: "",
            nodeGroupType: "",
            multiselectNodeGroupList: "",
            listOfNodegroups: [],
            requestCpuPrimary: "",
            limitCpuPrimary: "",
            requestCpuSecondary: "",
            limitCpuSecondary: "",
            requestMemPrimary: "",
            limitMemPrimary: "",
            requestMemSecondary: "",
            limitMemSecondary: "",
            tags: [{ name: "", value: "" }],
            labels: [{ name: "", value: "" }]
        }
    }

    const initialPostgresData = {
        serviceName: "",
        displayName: "",
        version: "",
        type: "Self Managed",
        serviceSelectedId: "",
        serviceEnabled: true,
        selfManaged: {
            storageType: "EBS",//Fixed to EBS for now
            serviceSelf: true,
            dbType: "",
            username: "",
            password: "",
            autoGenPassword: true,
            //enableMetrics: false,
            databaseName: "",
            storageSize: "",
            enableExporter: false,
            enableBackup: "",
            backupRetention: "",
            backupFrequency: "",
            backupDay: "",
            // backupTimingStart: "",
            // backupTimingEnd: "",
            backupInterval: "",
            backupTiming: "",
            enableCrossRegionBackup: false,
            crossRegionBackup: "",
            enableRestore: false,

            nodeGroupName: "",
            nodeGroupType: "",
            multiselectNodeGroupList: "",
            listOfNodegroups: [],
            requestCpuPrimary: "",
            limitCpuPrimary: "",
            requestCpuSecondary: "",
            limitCpuSecondary: "",
            requestMemPrimary: "",
            limitMemPrimary: "",
            requestMemSecondary: "",
            limitMemSecondary: "",
            tags: [{ name: "", value: "" }],
            labels: [{ name: "", value: "" }]
        },
        cloudManaged: {
            serviceAws: false,
            instanceFamily: "",
            instanceType: "",
            storageSize: "",
            subnetIds: {
                publicSubnets: [],
                privateSubnets: [],
            },
            dbType: "",
            enablePublicAccess: false,
            username: "",
            autoGenPassword: true,
            password: "",
            deletionProtection: false,
            databaseName: "",
            enableLogs: false,
            enableGeneralLogs: false,
            enableSlowLogs: false,
            logRetention: "",
            typeOfLogs: "",
            enableBackup: false,
            backupRetention: "",
            backupTimingStart: "",
            backupTimingEnd: "",
            backupLocation: "",
            kmsEncryption: "",
            tlsEncryption: false,
            tags: [{ name: "", value: "" }],
            labels: [{ name: "", value: "" }],
            maintenanceWindowStart: "",
            maintenanceWindowEnd: "",
            maintenanceWindowDay: "",

            nodeGroupName: "",
            nodeGroupType: "",
            multiselectNodeGroupList: "",
            listOfNodegroups: [],
        }
    };

    const snakeToCamel = {
        name: "displayName",
        record_prefix: "domainHostUrl",
        yaml_content: "yamlData"
    };
    let impFieldConvert = {
        "password": "password",
        "record_prefix": "domainHostUrl",
        "name": "displayName",
        "nodegroup_name": "nodeGroupName",
        "namespace": "clusterNamespace",
        "custom_user_password": "password",
        "master_password": "password",
        "custom_database_password": "password",
        "password": "password",
        "postgresql_password": "password",
        "container_registry_id": "containerRegistryId",
        "service_type": "endpointType"
    };

    let warnings = (removePrev, removeNext, importantFields, handlePrevious, handleNext, currentIdx) => {
        return [<div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", fontSize: "14px" }}>
                <img src={impIcon} style={{ height: "12px", width: "12px", marginRight: "5px" }} />This field is required.</div>
            <div style={{ display: "flex", alignItems: "center", marginTop: "5px" }}>
                {!removePrev && <Button style={{ display: "end", marginRight: "10px", padding: "0 5px", alignItems: "center", border: "0.5px solid #132f44", borderRadius: "5px", height: "fit-content" }} onClick={() => {
                    handlePrevious(importantFields, currentIdx);
                }} data-testId="mandatory-field-previous"><div style={{ display: "flex", alignItems: "center", padding: "0", fontSize: "12px" }}><img src={rightIcon} style={{ marginRight: "5px", height: "5px", width: "5px", transform: "rotate(180deg)" }} />Previous </div></Button>}
                {!removeNext && <Button style={{ display: "end", padding: "0 5px", alignItems: "center", border: "0.5px solid #132f44", borderRadius: "5px", height: "fit-content" }} onClick={() => {
                    handleNext(importantFields, currentIdx);
                }} data-testId="mandatory-field-next"><div style={{ display: "flex", alignItems: "center", padding: "0", fontSize: "12px" }}>Next <img src={rightIcon} style={{ marginLeft: "5px", height: "5px", width: "5px" }} /></div></Button>}
            </div>
        </div>];
    };
    const validateYamls = async (appList) => {
        console.log("My app list: ", appList);
        const promises = appList.map((val, idx) => val?.deployments?.deploymentOption === "yaml" && validateYaml(val.deployments[val.deployments.yamlType].yamlString)
            .then((doc) => ({ idx, serviceName: val.basicDetails.serviceName, data: doc }))
            .catch((error) => ({ idx, serviceName: val.basicDetails.serviceName, error: error })
            ));

        const results = await Promise.all(promises);
        console.log("My results: ", results);
        return results;
    }


    const handleSubmit = async () => {
        //let servicePayload = [];
        // create new env
        if (!addDep) {
            let allForms = [], allErrors = [];
            let myErrors = {};
            // let checked = true;
            let applicationServicesList = !cloneDep ? Object.values(applicationData) : Object.values(cloneApplicationData);
            console.log("applicationServicesList :", JSON.parse(JSON.stringify(applicationServicesList)));
            console.log("deploymentForms :", JSON.parse(JSON.stringify(deploymentForms)));
            // let servicePayload = [];


            applicationServicesList.map((val) => {
                if (val.serviceEnabled === true) {
                    const form = deploymentForms[val.basicDetails.serviceName];
                    if (form) {
                        allForms.push({
                            serviceSelected: val.basicDetails.serviceName,
                            form: form
                        });
                    }
                }
            });

            let yamls = await validateYamls(applicationServicesList);

            let yamlError = false;

            yamls.forEach((val, idx) => {
                if (val && val.data && idx === val.idx) {
                    applicationServicesList[idx].deployments.yamlData = [...val.data];
                }
                else if (val && applicationServicesList[idx].serviceEnabled) {
                    myErrors = {
                        ...myErrors,
                        [val.serviceName]: "Your YAML code provided is invalid."
                    };
                    allErrors = [...allErrors, {
                        serviceSelected: val.serviceName,
                        error: "Your YAML code provided is invalid."
                    }];
                    yamlError = true;
                }
            });

            console.log("MY YAMLS: ", yamls);

            let dataSourcesMySqlList = !cloneDep ? Object.values(dataSourcesData) : Object.values(cloneDataSourcesData);
            dataSourcesMySqlList.map((val) => {
                // Only validate if service is actually enabled (check both serviceEnabled and ensure it's true)
                if (val.serviceEnabled === true) {
                    console.log("val.displayName", val);
                    const form = deploymentForms[val.serviceName];
                    if (form) {
                        allForms.push({
                            serviceSelected: val.serviceName,
                            form: form
                        });
                    }
                }
            });

            console.log("Mysql values: ", allForms);

            let dataSourcesMongoList = !cloneDep ? Object.values(mongoDataSourcesData) : Object.values(cloneMongoDataSourcesData);
            dataSourcesMongoList.map((val) => {
                // Only validate if service is actually enabled (check both serviceEnabled and ensure it's true)
                if (val.serviceEnabled === true) {
                    console.log("val.displayName", val);
                    const form = deploymentForms[val.serviceName];
                    if (form) {
                        allForms.push({
                            serviceSelected: val.serviceName,
                            form: form
                        });
                    }
                }
            });

            let redisList = !cloneDep ? Object.values(redisData) : Object.values(cloneRedisData);
            console.log("Redis data: ",);
            redisList.map((val) => {
                // Only validate if service is actually enabled (check both serviceEnabled and ensure it's true)
                if (val.serviceEnabled === true) {
                    console.log("Val.data: ", val.cloudManaged.maintenanceWindowEnd);
                    const form = deploymentForms[val.serviceName];
                    if (form) {
                        allForms.push({
                            serviceSelected: val.serviceName,
                            form: form
                        });
                    }
                }
            });

            console.log("Entered here");

            let postgresList = !cloneDep ? Object.values(postgresDataSourcesData) : Object.values(clonePostgresDataSourcesData);
            postgresList.map((val) => {
                // Only validate if service is actually enabled (check both serviceEnabled and ensure it's true)
                if (val.serviceEnabled === true) {
                    const form = deploymentForms[val.serviceName];
                    if (form) {
                        allForms.push({
                            serviceSelected: val.serviceName,
                            form: form
                        });
                    }
                }
            });

            console.log("All forms: ", allForms);
            // trigger all services form validations

            for (const val of allForms) {
                if (!val.form) {
                    console.warn(`Form not found for service: ${val.serviceSelected}`);
                    continue;
                }
                try {
                    console.log("Try entered", val.form.getFieldsValue());
                    let variable = await val.form.validateFields();
                    console.log("ending validation...", variable);
                }
                catch (error) {
                    console.log("Catch entered");
                    allErrors = [...allErrors, {
                        serviceSelected: val.serviceSelected,
                        error: error
                    }];
                    myErrors = {
                        ...myErrors,
                        [val.serviceSelected]: {
                            error: error
                        }
                    }
                }
            }

            console.log("My errors: ", myErrors);
            setFormErrors(myErrors);

            // if there is an error, trigger alerts

            if (allErrors.length > 0) {
                if (yamlError) {
                    set_alertOptions(alertOptions => ({
                        status: true,
                        type: "error",
                        message: "Environment Creation failed",
                        description: "Please provide correct yaml file.",
                    }));

                    // opacity=0 after 10sec
                    setTimeout(() => {
                        set_alertOptions(alertOptions => ({
                            status: false,
                            type: "error",
                            message: "Environment Creation failed",
                            description: "Please provide correct yaml file.",
                        }));
                    }, 5 * 1000);
                }
                else {
                    console.error('Validation failed: ', allErrors);
                    set_alertOptions(alertOptions => ({
                        status: true,
                        type: "error",
                        message: "Environment Creation failed",
                        description: "Please fill all the fields for the enabled services.",
                    }));

                    // opacity=0 after 10sec
                    setTimeout(() => {
                        set_alertOptions(alertOptions => ({
                            status: false,
                            type: "error",
                            message: "Environment Creation failed",
                            description: "Please fill all the fields for the enabled services.",
                        }));
                    }, 5 * 1000);
                }
            }
            else {
                let days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                let myDepData = !cloneDep ? JSON.parse(localStorage.getItem("deploymentData")) : JSON.parse(localStorage.getItem("cloneDeploymentData"));
                if (myDepData && myDepData.projectName !== "" && myDepData.projectId !== 0) {
                    console.log("Validation succeeded");
                    //let servicePayload = [];
                    let applicationServicesList = !cloneDep ? Object.values(applicationData) : Object.values(cloneApplicationData);
                    console.log("My yamls: ", yamls);
                    yamls.forEach((val, idx) => {
                        if (val && val.data && idx === val.idx) {
                            applicationServicesList[idx].deployments.yamlData = [...val.data];
                        }
                    });
                    console.log("My application service: ", applicationServicesList);
                    let servicePayload = [];
                    applicationServicesList.map((val) => {
                        if (val.serviceEnabled === true) {
                            let myConfigVol = [];
                            let mySecretVol = [];
                            let myStorageVol = [];
                            const envList = val.environments || [];
                            let myEnvs = envList.length > 0 ? (envList.length === 1 ? (envList[0].name === "" && envList[0].value === "") ? [] : envList : envList) : envList;
                            let tags = {};
                            normalizeDeploymentTagsForPayload(val.deployments?.tags).forEach((tagItem) => {
                                if (tagItem.name !== "" && tagItem.value !== "") {
                                    tags[tagItem.name] = tagItem.value;
                                }
                            });
                            let labels = {};
                            const rawDeployLabels = val.deployments.labels;
                            if (Array.isArray(rawDeployLabels)) {
                                rawDeployLabels.forEach((item) => {
                                    if (item.name !== "" && item.value !== "") {
                                        labels[item.name] = item.value;
                                    }
                                });
                            } else if (rawDeployLabels && typeof rawDeployLabels === "object") {
                                Object.assign(labels, rawDeployLabels);
                            }
                            (val.volumes || []).map((ele) => {
                                if (ele.volumeType === "Configmap") {
                                    let rawStr = ele.config.createMountPath.split('/')
                                    let tempFilename = rawStr.pop()  // removing filename from rawStr
                                    let tempMountPath = rawStr.join("/")   // join for mountPath

                                    console.log("tempFilename :", tempFilename)
                                    console.log("tempMountPath :", tempMountPath)

                                    myConfigVol.push({
                                        "name": ele.config.configName,//"test-cm",
                                        "filename": tempFilename,//"index.yaml",
                                        "mountPath": tempMountPath,//"/app",
                                        "data": ele.config.createConfig//"this the configmap data...."
                                    });
                                }
                                else if (ele.volumeType === "Secrets") {
                                    let rawStrSecrets = ele.secrets.createSecretsMountPath.split('/')
                                    let tempFilenameSecrets = rawStrSecrets.pop()  // removing filename from rawStrSecrets
                                    let tempMountPathSecrets = rawStrSecrets.join("/")   // join for mountPath

                                    mySecretVol.push({
                                        "name": ele.secrets.secretsName,//"test-secretqs",
                                        "filename": tempFilenameSecrets,//"index.yaml",
                                        "mountPath": tempMountPathSecrets,//"/app",
                                        "data": ele.secrets.createSecrets//"this the configmap data...."
                                    });
                                }
                                else if (ele.volumeType === "Storage") {
                                    myStorageVol.push({
                                        "name": ele.storage.storageDes.storageName,//"test-secrets",
                                        "size": ele.storage.storageDes.size !== "" ? parseInt(ele.storage.storageDes.size) : 0,//+ 'Gi' : "",//"10Gi",
                                        "mountPath": ele.storage.storageDes.path,//"/app",
                                        "storageClass": ele.storage.storageDes.type.toLowerCase() === "efs" ? 'efs-sc' : ele.storage.storageDes.type.toLowerCase(),//"gp2",
                                        "accessMode": "ReadWriteOnce"
                                    });
                                }
                            });

                            const matchedProjectTask = currentProjectDep?.tasks?.find((item) => item.serviceId === val.serviceSelectedId);
                            let secret_name = matchedProjectTask?.secret_name;
                            let accountDetails = matchedProjectTask?.accountDetails;
                            console.log("accountDetails :", accountDetails)
                            console.log("App details: ", val, (val.deployments.endpointType === "Cluster IP" || val.deployments.serviceType === "Existing Load Balancer") ? "ClusterIP" : !val.deployments.endpointType ? "" : "LoadBalancer");
                            console.log("currentProjectDep: ", currentProjectDep);
                            servicePayload.push({
                                "name": val.basicDetails.appName,
                                "service_id": val.serviceSelectedId,//serviceSelected.id,
                                "cd_values_in_yaml": val.deployments.deploymentOption === "yaml",
                                "config": {
                                    "secret_details": accountDetails?.account_id ? {
                                        "secret_name": secret_name || "",
                                        "region": accountDetails?.region || "",
                                        "account_id": accountDetails?.account_id || "",
                                        "integration_id": accountDetails?.integration || "",
                                    } : null,
                                    //"container_registry_id": val.basicDetails.containerRegistryId.id,
                                    "docker_file": val.basicDetails.sourceType === "Container Registry" ? undefined : './' + val.basicDetails.dockerFilePath,
                                    "build_context": val.basicDetails.sourceType === "Container Registry" ? undefined : val.basicDetails.buildContext && val.basicDetails.buildContext !== "" ? "./" + val.basicDetails.buildContext : null,
                                    "app_source_type": val.basicDetails.sourceType === "Container Registry" ? 0 : 1,
                                    "container_registry_id": val.basicDetails.appSource === "PUBLIC" ? null : val.basicDetails.sourceType === "Container Registry" ? val.basicDetails.appSource?.id : val.basicDetails.containerRegistryId?.id ?? null,
                                    "container_registry_type": val.basicDetails.sourceType === "Container Registry" ? val.basicDetails.appSource === "PUBLIC" ? 0 : 1 : undefined,
                                    "repository_name":  val.basicDetails.sourceType === "Container Registry" ? val.basicDetails.imageRepo : cloneDep ? val.deployments.repositoryNameClone : undefined,
                                    "image_tag": val.basicDetails.sourceType === "Container Registry" ? val.basicDetails.imageTag : cloneDep ? val.deployments.imageTagClone : undefined,
                                    "architecture_type": val.basicDetails.architectureType,
                                    "port": val.deployments.deploymentOption === "visual" ? parseInt(val.deployments.port) : undefined,
                                    "create_storage": val.deployments.deploymentOption === "visual" ? myStorageVol.length > 0 ? "true" : "false" : undefined,
                                    "create_secret": val.deployments.deploymentOption === "visual" ? mySecretVol.length > 0 ? "true" : "false" : undefined,
                                    "ingress_enabled": val.deployments.deploymentOption === "visual" ? val.deployments.serviceType.includes("Existing Load Balancer") ? "true" : "false" : undefined,//val.deployments.customDomain === true ? "true" : "false",
                                    "ingress_https_enabled": val.deployments.deploymentOption === "visual" ? "true" : undefined,
                                    "ingress_host": val.deployments.deploymentOption === "visual" ? val.deployments.domainHostUrl === "" ? "" : val.deployments.domainHostUrl + '.' + val.deployments.dns : undefined,
                                    "zone_id": val.deployments.deploymentOption === "visual" ? (val.deployments.endpointType === "Cluster IP" ? null : val.deployments.hostedAccount) : undefined,
                                    "dns_record_type": val.deployments.deploymentOption === "visual" ? val.deployments.recordType : undefined,
                                    "hosted_zone": val.deployments.deploymentOption === "visual" ? (val.deployments.endpointType === "Cluster IP" ? "" : val.deployments.dns) : undefined,
                                    "ingress_path": val.deployments.deploymentOption === "visual" ? (val.deployments.endpointType === "Cluster IP" ? "/" : (val.deployments.gatewayHttps.gPath === "" ? "/" : val.deployments.gatewayHttps.gPath)) : undefined,
                                    "env_variables": myEnvs,
                                    "record_prefix": val.deployments.deploymentOption === "visual" ? val.deployments.serviceType.includes("Existing Load Balancer") ? val.deployments.domainHostUrl : "" : undefined,
                                    "service_type": val.deployments.deploymentOption === "visual" ? (val.deployments.endpointType === "Cluster IP" || val.deployments.serviceType.includes("Existing Load Balancer")) ? "ClusterIP" : !val.deployments.endpointType ? "" : "LoadBalancer" : undefined,//serviceSelected.type,
                                    "service_protocol": val.deployments.deploymentOption === "visual" ? "TCP" : undefined,
                                    "enable_plaintext_env": myEnvs.length > 0 ? "true" : "false",
                                    "enable_secret_env": myEnvs.length > 0 ? "true" : "false",
                                    "vcs_branch": val.basicDetails.branch === "" ? null : val.basicDetails.branch,
                                    "secretfile_enabled": val.deployments.deploymentOption === "visual" ? mySecretVol.length > 0 ? "true" : "false" : undefined,
                                    "create_configmap": val.deployments.deploymentOption === "visual" ? myConfigVol.length > 0 ? "true" : "false" : undefined,
                                    "cpu_request": val.deployments.deploymentOption === "visual" ? parseInt(val.deployments.resources.cpu.requestCpu) : undefined, //+ 'm',
                                    "cpu_limit": val.deployments.deploymentOption === "visual" ? parseInt(val.deployments.resources.cpu.limitCpu) : undefined, //+ 'm',
                                    "memory_request": val.deployments.deploymentOption === "visual" ? parseInt(val.deployments.resources.memory.requestMem) : undefined, //+ 'Mi',
                                    "memory_limit": val.deployments.deploymentOption === "visual" ? parseInt(val.deployments.resources.memory.limitMem) : undefined, //+ 'Mi',
                                    "target_cpu_utilization": val.deployments.deploymentOption === "visual" ? val.deployments.autoScaling.autoScalingData.autoScalingParameter.cpuAuto === "" ? 0 : parseInt(val.deployments.autoScaling.autoScalingData.autoScalingParameter.cpuAuto) : undefined, //+ '%',
                                    "target_memory_utilization": val.deployments.deploymentOption === "visual" ? val.deployments.autoScaling.autoScalingData.autoScalingParameter.memoryAuto === "" ? 0 : parseInt(val.deployments.autoScaling.autoScalingData.autoScalingParameter.memoryAuto) : undefined, //+ '%',
                                    "enable_autoscaling": val.deployments.deploymentOption === "visual"
                                        ? String(val.deployments.autoScaling?.enabled === true)
                                        : undefined,
                                    "maximum_replicas": val.deployments.deploymentOption === "visual" ? val.deployments.autoScaling.autoScalingData.maximumReplica || val.deployments.autoScaling.autoScalingData.maximumReplica === 0 ? val.deployments.autoScaling.autoScalingData.maximumReplica.toString() : "" : undefined,
                                    "minimum_replicas": val.deployments.deploymentOption === "visual" ? val.deployments.autoScaling.autoScalingData.minimumReplica || val.deployments.autoScaling.autoScalingData.minimumReplica === 0 ? val.deployments.autoScaling.autoScalingData.minimumReplica.toString() : "" : undefined,
                                    "config_files_config": val.deployments.deploymentOption === "visual" ? myConfigVol.length === 1 ? (myConfigVol[0].name === "" ? [] : myConfigVol) : myConfigVol : undefined,
                                    "storages_config": val.deployments.deploymentOption === "visual" ? myStorageVol.length === 1 ? (myStorageVol[0].name === "" ? [] : myStorageVol) : myStorageVol : undefined,
                                    "secret_files_config": val.deployments.deploymentOption === "visual" ? mySecretVol.length === 1 ? (mySecretVol[0].name === "" ? [] : mySecretVol) : mySecretVol : undefined,
                                    "labels": labels,
                                    "health_check_path": val.deployments.deploymentOption === "visual" ? val.deployments.healthCheckPath : undefined,
                                    "auto_trigger": String(val.basicDetails.enableAutoTrigger ?? false),
                                    "create_cron": val.deployments.deploymentOption === "visual" ? (val.deployments.taskSchedulers?.crons ? "true" : "false") : "false",
                                    "create_queue_worker": val.deployments.deploymentOption === "visual" ? (val.deployments.taskSchedulers?.queuesAndWorkers ? "true" : "false") : "false",
                                    "cron_command": val.deployments.deploymentOption === "visual" ? String(val.deployments.taskSchedulers?.entryPointCommandCrons ?? "") : "",
                                    "queue_worker_command": val.deployments.deploymentOption === "visual" ? String(val.deployments.taskSchedulers?.entryPointCommandQueues ?? "") : "",
                                    "pass_env_vars_at_build_time": String(
                                        Boolean(
                                            currentProjectDep?.tasks
                                                ?.find((x) => x.serviceId === val.serviceSelectedId)
                                                ?.envs?.some(
                                                    (x) =>
                                                        Array.isArray(x?.injectAt) &&
                                                        x.injectAt.includes("Build Time") &&
                                                        x.lastAction !== "Delete"
                                                )
                                        )
                                    ),
                                    "nodegroup_name": val.deployments.deploymentOption === "visual" ? val.deployments.nodeGroupName : undefined,
                                    "nodegroup_type": val.deployments.deploymentOption === "visual" ? val.deployments.nodeGroupType : undefined,
                                    "list_of_nodegroups": val.deployments.deploymentOption === "visual" ? (myDepData?.provisioner === "imported" ? [] : val.deployments.listOfNodegroups) : undefined,
                                    "ingress_alb_enabled": val.deployments.deploymentOption === "visual" ? val.deployments.serviceType === "Existing Load Balancer(ALB Ingress)" ? "true" : "false" : undefined,//deploymentData.ingressAlbEnabled.toString(),
                                    "acm_certificate": val.deployments.deploymentOption === "visual" && val.deployments.serviceType === "Existing Load Balancer(ALB Ingress)" ? acmCertificateForPayload(val.deployments.acmCertificate) : undefined,
                                    "yaml_content": val.deployments.deploymentOption === "yaml" ? val.deployments.yamlData : undefined,
                                    "yaml_kind": val.deployments.deploymentOption === "yaml" ? val.deployments.yamlType : undefined,
                                    "labels": labels
                                },
                                //"context": val.basicDetails.context,
                                // "environments.plaintext.values": appData.envPlainVars,
                                // "environments.secret.values": appData.envSecretVars,
                                // "configmap_file_name": val.volumes[0].config.configFileName,
                                // "configmap_content": val.volumes[0].config.createConfig,
                                // "configmap_file_mountpath": val.volumes[0].config.createMountPath,
                                // "storage_size": val.volumes[0].storage.storageDes.size,
                                // "VolumemountPath": val.volumes[0].storage.storageDes.path,
                                // "storage_class": val.volumes[0].storage.storageDes.type //will be changed later
                            });
                        }
                    });

                    let dataSourcesMySqlAws = !cloneDep ? Object.values(dataSourcesData) : Object.values(cloneDataSourcesData);
                    console.log("data sources: ", dataSourcesMySqlAws);

                    dataSourcesMySqlAws.map((val) => {
                        if (val.serviceEnabled && val.cloudManaged.serviceAws) {
                            servicePayload.push({
                                "name": val.displayName,//"mysql",
                                "service_id": val.serviceSelectedId,//40,
                                "config": {
                                    "db_type": val.type === "AWS Managed MySQL" ? "aws-rds-managed-mysql" : "aws-aurora-managed-mysql",
                                    "mysql_instance_class": val.cloudManaged.instanceType,//"db.t3.micro" , 
                                    "Instance_family": val.cloudManaged.instanceFamily,
                                    "mysql_engine_version": val.version,
                                    "tags": val.cloudManaged.tags.length === 1 ? (val.cloudManaged.tags[0].name === "" || val.cloudManaged.tags[0].value === "") ? [] : val.cloudManaged.tags : val.cloudManaged.tags,
                                    "database_name": val.cloudManaged.databaseName,//"nodeapp",        
                                    "enable_multi_az": val.cloudManaged.dbType === "Single DB Instance" ? "false" : "true", //"false",
                                    "master_username": val.cloudManaged.username,//"abhi",
                                    "storage_size": parseInt(val.cloudManaged.storageSize),//"30",       
                                    "enable_public_access": val.cloudManaged.enablePublicAccess ? "true" : "false",//"true",
                                    "skip_final_snapshot": "true",
                                    "backup_window": (val.cloudManaged.backupTimingStart === "" || val.cloudManaged.backupTimingEnd === "") ? "" : val.cloudManaged.backupTimingStart + '-' + val.cloudManaged.backupTimingEnd,//"03:00-06:00",
                                    "backup_region": val.cloudManaged.backupRegion,
                                    "maintenance_window": (val.cloudManaged.maintenanceWindowStart === "" || val.cloudManaged.maintenanceWindowEnd === "") ? "" : val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowStart + '-' + val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowEnd,//"Mon:00:00-Mon:03:00",
                                    "enable_deletion_protection": val.cloudManaged.deletionProtection ? "true" : "false",//"false",
                                    "backup_retention_period": val.cloudManaged.backupRetention === "" ? "0" : val.cloudManaged.backupRetention,//"7",
                                    "enable_general_log": val.cloudManaged.enableGeneralLogs ? "true" : "false",//"true",
                                    "enable_slow_query_log": val.cloudManaged.enableSlowLogs ? "true" : "false",//"true",
                                    "create_random_password": val.cloudManaged.autoGenPassword ? "true" : "false",//"true"
                                    "master_password": val.cloudManaged.password,
                                    "log_retention_period": val.cloudManaged.logRetention,
                                    //"enable_backup": val.cloudManaged.enableBackup ? "true" : 'false',
                                    "private_subnet_ids": val.cloudManaged.subnetIds.privateSubnets,
                                    "public_subnet_ids": val.cloudManaged.subnetIds.publicSubnets,
                                }
                            });
                        }
                    });

                    let dataSourcesMySqlSelf = !cloneDep ? Object.values(dataSourcesData) : Object.values(cloneDataSourcesData);
                    console.log("Mysql self: ", dataSourcesMySqlSelf);
                    dataSourcesMySqlSelf.map((val) => {
                        if (val.serviceEnabled && val.selfManaged.serviceSelf) {
                            console.log("Executes");
            let labelsMysql = {};
            const rawMysqlLabels = val.selfManaged.labels;
            if (Array.isArray(rawMysqlLabels)) {
                rawMysqlLabels.forEach((item) => {
                    if (item.name !== "" && item.value !== "") {
                        labelsMysql[item.name] = item.value;
                    }
                });
            } else if (rawMysqlLabels && typeof rawMysqlLabels === "object") {
                Object.assign(labelsMysql, rawMysqlLabels);
            }
                            // Check multiple possible fields for storageSize (storageSize, masterStorageSize)
                            const rawStorageSize = val.selfManaged.storageSize || val.selfManaged.masterStorageSize || "";
                            // Only parse if we have a valid non-empty value
                            const storageSizeInt = rawStorageSize && rawStorageSize !== "" && !isNaN(parseInt(rawStorageSize))
                                ? parseInt(rawStorageSize) 
                                : undefined;
                            // Ensure dbType (replica count) is a valid integer, defaulting to 0 if empty
                            const replicaCount = val.selfManaged.dbType !== undefined && val.selfManaged.dbType !== null && val.selfManaged.dbType !== "" 
                                ? parseInt(val.selfManaged.dbType) 
                                : 0;
                            
                            servicePayload.push({
                                "name": val.displayName,
                                "service_id": val.serviceSelectedId,//3,
                                "config": {
                                    "db_type": "self-managed-mysql",
                                    //"architecture": val.selfManaged.dbType,//"standalone",
                                    "storage_class_name": myDepData?.provisioner !== "atmosly" ? "" : val.selfManaged.storageType.toLowerCase() === "efs" ? "efs-sc" : val.selfManaged.storageType.toLowerCase(),//"gp2",
                                    "username": val.selfManaged.username,//"admin",
                                    ...(storageSizeInt !== undefined ? {
                                        "master_db_volume_size": storageSizeInt,
                                        "secondary_db_volume_size": storageSizeInt,
                                    } : {}),
                                    "secondary_db_replica_count": replicaCount,
                                    "backup_enabled": val.selfManaged.enableBackup ? "true" : "false",//"false",
                                    "bucket_uri": "",
                                    "enable_cross_region_backup": val.selfManaged.enableCrossRegionBackup.toString(),
                                    "bucket_region": val.selfManaged.crossRegionBackup,//"",
                                    //"backup_cron": "* * * * *",
                                    "restore_db": "false",
                                    "exporter_enable": val.selfManaged.enableMetrics ? "true" : "false",//"true",
                                    "app_version": val.version,
                                    "custom_credentials_enabled": !val.selfManaged.autoGenPassword ? "true" : "false",//"true",
                                    "store_password_to_secret_manager": "false",
                                    "custom_user_password": val.selfManaged.password,//"password",
                                    "backup_frequency": val.selfManaged.backupFrequency.toLowerCase(),//"hourly",
                                    "backup_interval": val.selfManaged.backupFrequency === "Weekly" ? days.indexOf(val.selfManaged.backupDay) : val.selfManaged.backupFrequency !== "Hourly" ? 1 : val.selfManaged.backupInterval,//3,
                                    "backup_time": val.selfManaged.backupTiming,
                                    "database_name": val.selfManaged.databaseName,
                                    "restore_file_name": "",
                                    "list_of_nodegroups": myDepData?.provisioner === "imported" ? [] : val.selfManaged.listOfNodegroups,
                                    "restore_db": val.selfManaged.enableRestore ? "true" : "false",
                                    "primary_cpu_limit": val.selfManaged.limitCpuPrimary,
                                    "primary_memory_limit": val.selfManaged.limitMemPrimary,
                                    "primary_cpu_request": val.selfManaged.requestCpuPrimary,
                                    "primary_memory_request": val.selfManaged.requestMemPrimary,
                                    "secondary_cpu_limit": val.selfManaged.limitCpuSecondary,
                                    "secondary_memory_limit": val.selfManaged.limitMemSecondary,
                                    "secondary_cpu_request": val.selfManaged.requestCpuSecondary,
                                    "secondary_memory_request": val.selfManaged.requestMemSecondary,
                                    "labels": labelsMysql
                                }
                            });
                        }
                    });

                    let dataSourcesMongoSelf = !cloneDep ? Object.values(mongoDataSourcesData) : Object.values(cloneMongoDataSourcesData);
                    console.log("Mysql self: ", dataSourcesMongoSelf);
                    dataSourcesMongoSelf.map((val) => {
                        if (val.serviceEnabled && val.selfManaged.serviceSelf) {
            let labelsMongo = {};
            const rawMongoLabels = val.selfManaged.labels;
            if (Array.isArray(rawMongoLabels)) {
                rawMongoLabels.forEach((item) => {
                    if (item.name !== "" && item.value !== "") {
                        labelsMongo[item.name] = item.value;
                    }
                });
            } else if (rawMongoLabels && typeof rawMongoLabels === "object") {
                Object.assign(labelsMongo, rawMongoLabels);
            }
                            // Check multiple possible fields for storageSize
                            const rawStorageSize = val.selfManaged.storageSize || "";
                            // Only parse if we have a valid non-empty value
                            const storageSizeInt = rawStorageSize && rawStorageSize !== "" && !isNaN(parseInt(rawStorageSize))
                                ? parseInt(rawStorageSize) 
                                : undefined;
                            // Ensure dbType (replica count) is a valid integer, defaulting to 0 if empty
                            const replicaCount = val.selfManaged.dbType !== undefined && val.selfManaged.dbType !== null && val.selfManaged.dbType !== "" 
                                ? parseInt(val.selfManaged.dbType) 
                                : 0;
                            
                            servicePayload.push({
                                "name": val.displayName,//"dskldss",
                                "service_id": val.serviceSelectedId,//6,
                                "type": "self-managed-mongodb",
                                "config": {
                                    "db_type": "self-managed-mongodb",
                                    //"architecture": val.selfManaged.dbType.toLowerCase(),//"standalone",
                                    "storage_class_name": myDepData?.provisioner !== "atmosly" ? "" : val.selfManaged.storageType.toLowerCase() === "efs" ? "efs-sc" : val.selfManaged.storageType.toLowerCase(),                            //"username": val.selfManaged.username,//"admin",
                                    ...(storageSizeInt !== undefined ? {
                                        "volume_size": storageSizeInt,
                                    } : {}),
                                    //"secondary_db_volume_size": val.selfManaged.storageSize === "" ? "" : val.selfManaged.storageSize + 'Gi',//"10Gi",
                                    "replica_count": replicaCount,
                                    //"replica_count": "1",
                                    "backup_enabled": val.selfManaged.enableBackup ? "true" : "false",//"false", //backup_enable
                                    "bucket_uri": "",
                                    "enable_cross_region_backup": val.selfManaged.enableCrossRegionBackup.toString(),
                                    "bucket_region": val.selfManaged.crossRegionBackup,//"", //bucket_region
                                    "restore_db": "false",
                                    "exporter_enable": val.selfManaged.enableMetrics ? "true" : "false",//"true",
                                    //"mongodb_version": "",//"5.0.8-debian-10-r9", //later
                                    "app_version": val.version,
                                    "custom_credentials_enabled": !val.selfManaged.autoGenPassword ? "true" : "false",//"true",
                                    "store_password_to_secret_manager": "false",
                                    //"root_password": val.selfManaged.password,//"password",
                                    "backup_frequency": val.selfManaged.backupFrequency.toLowerCase(),//"hourly",
                                    "backup_interval": val.selfManaged.backupFrequency === "Weekly" ? days.indexOf(val.selfManaged.backupDay) : val.selfManaged.backupFrequency !== "Hourly" ? 1 : val.selfManaged.backupInterval,//3,
                                    "backup_time": val.selfManaged.backupTiming,
                                    "custom_database_name": val.selfManaged.databaseName,
                                    "custom_database_password": val.selfManaged.password,
                                    "custom_database_username": val.selfManaged.username,//"abhi"
                                    "list_of_nodegroups": myDepData?.provisioner === "imported" ? [] : val.selfManaged.listOfNodegroups,
                                    "restore_db": val.selfManaged.enableRestore ? "true" : "false",
                                    "primary_cpu_limit": val.selfManaged.limitCpuPrimary,
                                    "primary_memory_limit": val.selfManaged.limitMemPrimary,
                                    "primary_cpu_request": val.selfManaged.requestCpuPrimary,
                                    "primary_memory_request": val.selfManaged.requestMemPrimary,
                                    "labels": labelsMongo
                                }
                            });
                        }
                    });

                    let redisCloud = !cloneDep ? Object.values(redisData) : Object.values(cloneRedisData);
                    redisCloud.map((val) => {
                        if (val.serviceEnabled && val.cloudManaged.serviceCloud) {
                            servicePayload.push({
                                "name": val.displayName,
                                "service_id": val.serviceSelectedId,
                                //"type": "elasticache-redis",
                                "config": {
                                    "db_type": "elasticache-redis",
                                    "node_type": val.cloudManaged.instanceType,
                                    "Instance_family": val.cloudManaged.instanceFamily,
                                    //"family": "redis6.x",//will append the version to redis
                                    "num_cache_nodes": val.cloudManaged.noOfReplica.toString(),//"",
                                    "multi_az_enabled": val.cloudManaged.enableMultiAz ? "true" : "false",
                                    "automatic_failover_enabled": val.cloudManaged.enableAutomaticFailover ? "true" : "false",
                                    "at_rest_encryption_enabled": val.cloudManaged.enableRestEncryption ? "true" : "false",
                                    // "snapshot_retention_limit": val.cloudManaged.backupRetention === "" ? "0" : val.cloudManaged.backupRetention,//val.cloudManaged.backupRetention ? "true" : "false",
                                    "transit_encryption_enabled": val.cloudManaged.enableTransitEncryption ? "true" : "false",
                                    // "snapshot_window": (val.cloudManaged.backupTimingStart === "" || val.cloudManaged.backupTimingEnd === "") ? "" : val.cloudManaged.backupTimingStart + '-' + val.cloudManaged.backupTimingEnd,
                                    "maintenance_window": (val.cloudManaged.maintenanceWindowStart === "" || val.cloudManaged.maintenanceWindowEnd === "") ? "" : val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowStart + '-' + val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowEnd,
                                    "engine_version": val.version,
                                    "private_subnet_ids": val.cloudManaged.subnetIds.privateSubnets,
                                    "public_subnet_ids": val.cloudManaged.subnetIds.publicSubnets,
                                }
                            });
                        }
                    });

                    let redisSelf = !cloneDep ? Object.values(redisData) : Object.values(cloneRedisData);
                    redisSelf.map((val) => {
                        if (val.serviceEnabled && val.selfManaged.serviceSelf) {
            let labelsRedis = {};
            const rawRedisLabels = val.selfManaged.labels;
            if (Array.isArray(rawRedisLabels)) {
                rawRedisLabels.forEach((item) => {
                    if (item.name !== "" && item.value !== "") {
                        labelsRedis[item.name] = item.value;
                    }
                });
            } else if (rawRedisLabels && typeof rawRedisLabels === "object") {
                Object.assign(labelsRedis, rawRedisLabels);
            }
                            // Check multiple possible fields for storageSize
                            const rawStorageSize = val.selfManaged.storageSize || "";
                            // Only parse if we have a valid non-empty value
                            const storageSizeInt = rawStorageSize && rawStorageSize !== "" && !isNaN(parseInt(rawStorageSize))
                                ? parseInt(rawStorageSize) 
                                : undefined;
                            // Ensure noOfReplica (replica count) is a valid integer, defaulting to 0 if empty
                            const replicaCount = val.selfManaged.noOfReplica !== undefined && val.selfManaged.noOfReplica !== null && val.selfManaged.noOfReplica !== "" 
                                ? parseInt(val.selfManaged.noOfReplica) 
                                : 0;
                            
                            servicePayload.push({
                                "name": val.displayName,//"mks3",
                                "service_id": val.serviceSelectedId,//5,
                                //"type": "self-managed-redis",
                                "config": {
                                    "db_type": "self-managed-redis",
                                    //"architecture": "replication", //will add later
                                    //"replicacount": "1", //will add later
                                    "storage_class_name": myDepData?.provisioner !== "atmosly" ? "" : val.selfManaged.storageType.toLowerCase() === "efs" ? "efs-sc" : val.selfManaged.storageType.toLowerCase(),//"gp2",
                                    //"enable_metrics": val.selfManaged.enableMetrics ? "true" : "false",//"true",
                                    ...(storageSizeInt !== undefined ? {
                                        "slave_volume_size": storageSizeInt,
                                        "master_volume_size": storageSizeInt,
                                    } : {}),
                                    "slave_replica_count": replicaCount,//"2", //will add later
                                    //"monitoring_enabled": "true", //will be added later
                                    "app_version": val.version,//"6.2.7-debian-11-r11", //version list update required
                                    "store_password_to_secret_manager": "false",
                                    "custom_credentials_enabled": val.selfManaged.authEnabled ? !val.selfManaged.autoGenPassword ? "true" : "false" : undefined,//"true",
                                    "password": val.selfManaged.authEnabled ? val.selfManaged.password : undefined,
                                    "grafana_monitoring_enabled": val.selfManaged.enableMetrics ? "true" : "false",
                                    "list_of_nodegroups": myDepData?.provisioner === "imported" ? [] : val.selfManaged.listOfNodegroups,
                                    "auth_enabled": val.selfManaged.authEnabled.toString(),
                                    "primary_cpu_limit": val.selfManaged.limitCpuPrimary,
                                    "primary_memory_limit": val.selfManaged.limitMemPrimary,
                                    "primary_cpu_request": val.selfManaged.requestCpuPrimary,
                                    "primary_memory_request": val.selfManaged.requestMemPrimary,
                                    "secondary_cpu_limit": val.selfManaged.limitCpuSecondary,
                                    "secondary_memory_limit": val.selfManaged.limitMemSecondary,
                                    "secondary_cpu_request": val.selfManaged.requestCpuSecondary,
                                    "secondary_memory_request": val.selfManaged.requestMemSecondary,
                                    "labels": labelsRedis
                                }
                            });
                        }
                    });

                    let postgresAws = !cloneDep ? Object.values(postgresDataSourcesData) : Object.values(clonePostgresDataSourcesData);
                    postgresAws.map((val) => {
                        if (val.serviceEnabled && val.cloudManaged.serviceAws) {
                            servicePayload.push({
                                "name": val.displayName,//"mysql",
                                "service_id": val.serviceSelectedId,//40,
                                "config": {
                                    "db_type": val.type === "AWS Managed Postgres" ? "aws-rds-managed-postgres" : "aws-aurora-managed-postgres",
                                    "instance_type": val.cloudManaged.instanceType,//"db.t3.micro" , 
                                    "Instance_family": val.cloudManaged.instanceFamily,
                                    "engine_version": val.version,
                                    "tags": val.cloudManaged.tags.length === 1 ? (val.cloudManaged.tags[0].name === "" || val.cloudManaged.tags[0].value === "") ? [] : val.cloudManaged.tags : val.cloudManaged.tags,
                                    "database_name": val.cloudManaged.databaseName,//"nodeapp",        
                                    "enable_multi_az": val.cloudManaged.dbType === "Single DB Instance" ? "false" : "true", //"false",
                                    "master_username": val.cloudManaged.username,//"abhi",
                                    "storage_size": parseInt(val.cloudManaged.storageSize),//"30",       
                                    "enable_public_access": val.cloudManaged.enablePublicAccess ? "true" : "false",//"true",
                                    "skip_final_snapshot": "true",
                                    "backup_window": val.type === "AWS Aurora Managed Postgres" ? (val.cloudManaged.backupTimingStart === "" || val.cloudManaged.backupTimingEnd === "") ? "" : val.cloudManaged.backupTimingStart + '-' + val.cloudManaged.backupTimingEnd :
                                        (val.cloudManaged.backupTimingStart === "" || val.cloudManaged.backupTimingEnd === "") ? "" : val.cloudManaged.backupTimingStart + '-' + val.cloudManaged.backupTimingEnd,//"03:00-06:00",
                                    "maintenance_window": (val.cloudManaged.maintenanceWindowStart === "" || val.cloudManaged.maintenanceWindowEnd === "") ? "" : val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowStart + '-' + val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowEnd,//"Mon:00:00-Mon:03:00",
                                    "enable_deletion_protection": val.cloudManaged.deletionProtection ? "true" : "false",//"false",
                                    "backup_retention_period": val.cloudManaged.backupRetention === "" ? "0" : val.cloudManaged.backupRetention,//"7",
                                    "enable_general_log": val.cloudManaged.enableGeneralLogs ? "true" : "false",//"true",
                                    "enable_slow_query_log": val.cloudManaged.enableSlowLogs ? "true" : "false",//"true",
                                    "create_random_password": val.cloudManaged.autoGenPassword ? "true" : "false",//"true"
                                    "master_password": val.cloudManaged.password,
                                    "log_retention_period": val.cloudManaged.logRetention,
                                    //"enable_backup": val.cloudManaged.enableBackup ? "true" : 'false'
                                    "private_subnet_ids": val.cloudManaged.subnetIds.privateSubnets,
                                    "public_subnet_ids": val.cloudManaged.subnetIds.publicSubnets,
                                }
                            });
                        }
                    })

                    let postgresSelf = !cloneDep ? Object.values(postgresDataSourcesData) : Object.values(clonePostgresDataSourcesData);
                    postgresSelf.map((val) => {
                        if (val.serviceEnabled && val.selfManaged.serviceSelf) {
            let labelsPostgres = {};
            const rawPostgresLabels = val.selfManaged.labels;
            if (Array.isArray(rawPostgresLabels)) {
                rawPostgresLabels.forEach((item) => {
                    if (item.name !== "" && item.value !== "") {
                        labelsPostgres[item.name] = item.value;
                    }
                });
            } else if (rawPostgresLabels && typeof rawPostgresLabels === "object") {
                Object.assign(labelsPostgres, rawPostgresLabels);
            }
                            // Check multiple possible fields for storageSize
                            const rawStorageSize = val.selfManaged.storageSize || "";
                            // Only parse if we have a valid non-empty value
                            const storageSizeInt = rawStorageSize && rawStorageSize !== "" && !isNaN(parseInt(rawStorageSize))
                                ? parseInt(rawStorageSize) 
                                : undefined;
                            // Ensure dbType (replica count) is a valid integer, defaulting to 0 if empty
                            const replicaCount = val.selfManaged.dbType !== undefined && val.selfManaged.dbType !== null && val.selfManaged.dbType !== "" 
                                ? parseInt(val.selfManaged.dbType) 
                                : 0;
                            
                            servicePayload.push({
                                "name": val.displayName,//"mks2",
                                "service_id": val.serviceSelectedId,//69,
                                "config": {
                                    "db_type": "self-managed-postgresql",
                                    "postgresql_exporter_enabled": val.selfManaged.enableExporter ? "true" : "false",//"false",
                                    "replicacount": replicaCount,//"1",
                                    "storage_class_name": myDepData?.provisioner !== "atmosly" ? "" : val.selfManaged.storageType.toLowerCase() === "efs" ? "efs-sc" : val.selfManaged.storageType.toLowerCase(),//"gp2",
                                    "custom_credentials_enabled": !val.selfManaged.autoGenPassword ? "true" : "false",//"true",
                                    "postgresql_password": val.selfManaged.password, //"",
                                    "app_version": val.version,//"16.0.0",
                                    "database_name": val.selfManaged.databaseName,//"atmosly",
                                    ...(storageSizeInt !== undefined ? {
                                        "storage_size": storageSizeInt,
                                    } : {}),//"10Gi"
                                    "postgresql_username": val.selfManaged.username,
                                    "backup_enabled": val.selfManaged.enableBackup ? "true" : "false",
                                    "backup_frequency": val.selfManaged.backupFrequency.toLowerCase(),
                                    "backup_interval": val.selfManaged.backupFrequency === "Weekly" ? days.indexOf(val.selfManaged.backupDay) : val.selfManaged.backupFrequency !== "Hourly" ? 1 : val.selfManaged.backupInterval,
                                    "backup_time": val.selfManaged.backupTiming,
                                    "enable_cross_region_backup": val.selfManaged.enableCrossRegionBackup.toString(),
                                    "bucket_region": val.selfManaged.crossRegionBackup,
                                    "list_of_nodegroups": myDepData?.provisioner === "imported" ? [] : val.selfManaged.listOfNodegroups,
                                    "restore_db": val.selfManaged.enableRestore ? "true" : "false",
                                    "primary_cpu_limit": val.selfManaged.limitCpuPrimary,
                                    "primary_memory_limit": val.selfManaged.limitMemPrimary,
                                    "primary_cpu_request": val.selfManaged.requestCpuPrimary,
                                    "primary_memory_request": val.selfManaged.requestMemPrimary,
                                    "secondary_cpu_limit": val.selfManaged.limitCpuSecondary,
                                    "secondary_memory_limit": val.selfManaged.limitMemSecondary,
                                    "secondary_cpu_request": val.selfManaged.requestCpuSecondary,
                                    "secondary_memory_request": val.selfManaged.requestMemSecondary,
                                    "labels": labelsPostgres
                                }
                            });
                        }
                    });

                    console.log("Payload", servicePayload);

                    if (servicePayload.length === 0) {
                        set_alertOptions(alertOptions => ({
                            status: true,
                            type: "error",
                            message: "Environment Creation failed.",
                            description: "Please enable at least one service in order to proceed.",
                        }));

                        // opacity=0 after 10sec
                        setTimeout(() => {
                            set_alertOptions(alertOptions => ({
                                status: false,
                                type: "error",
                                message: "Environment Creation failed.",
                                description: "Please enable at least one service in order to proceed.",
                            }));
                        }, 5 * 1000);
                    }
                    else {
                        loadDispatch({ loader: "createDep", status: "loading", nestLevel: 0 });
                        // console.log("Entered else");
                        let deploymentLabels = {};
                        !cloneDep ? deploymentData.tags.forEach((val, idx) => {
                            if (val.name !== "" && val.value !== "") {
                                deploymentLabels[val.name] = val.value;
                            }
                        }) : cloneDeploymentData.tags.forEach((val, idx) => {
                            if (val.name !== "" && val.value !== "") {
                                deploymentLabels[val.name] = val.value;
                            }
                        });
                        console.log("Final payload: ", servicePayload);
                        // loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                        // The backend answers validation failures with HTTP 400, so axios rejects
                        // and this handler has to run for the rejection too — otherwise every
                        // server-side error below collapses into a generic "Something went wrong".
                        const handleCreateEnvResponse = (res) => {
                            let data = res.data;
                            console.log("Enters");
                            if (data.status) {
                                console.log("Entered");
                                loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                                setAddDep(false);
                                setUpdateDep(false);
                                setCloneDep(false);
                                localStorage.setItem('isClone', JSON.stringify(false));
                                localStorage.setItem('updateDep', JSON.stringify(false));
                                localStorage.setItem('addDep', JSON.stringify(false));
                                localStorage.setItem('stopFetch', JSON.stringify(false));
                                navigate(`/environments/environment/${data.data.env_unique_id}/env-overview`, {
                                    state: {
                                        from: "/environments/services"
                                    }
                                });
                                if (!cloneDep) {
                                    setApplicationData({});
                                    setDataSourcesData({});
                                    setMongoDataSourcesData({});
                                    setRedisData({});
                                    setServicesEnabled({});
                                    setSelectedProject({});
                                    setPostgresDataSourcesData({});
                                    setContainerRegistries({});
                                    setCurrentProjectDep({});
                                    setDeploymentData({ ...initialDeploymentData });
                                    setServiceCategories({});
                                    RemoveEnvironmentFromLocal();
                                }
                                else {
                                    handleDeploymentLogout();
                                    RemoveCloneEnvironmentFromLocal();
                                }
                            }
                            // for finding server side errors related to dynamic fields
                            else if (data.error_details && (data.error_details.cluster_unique_id || data.error_details.name || data.error_details.namespace || data.error_details.account_id || data.error_details.services?.[0]?.service_id)) {
                                loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });

                                set_alertOptions(alertOptions => ({
                                    status: true,
                                    type: "error",
                                    message: "Environment Creation failed.",
                                    description: data.error_details.cluster_unique_id ? data.error_details.cluster_unique_id : data.error_details.name ? data.error_details.name : data.error_details.services?.[0]?.service_id ? data.error_details.services?.[0]?.service_id : data.error_details.account_id ? data.error_details.account_id : data.error_details.namespace,
                                }));

                                // opacity=0 after 10sec
                                setTimeout(() => {
                                    set_alertOptions(alertOptions => ({
                                        status: false,
                                        type: "error",
                                        message: "Environment Creation failed.",
                                        description: data.error_details.cluster_unique_id ? data.error_details.cluster_unique_id : data.error_details.name ? data.error_details.name : data.error_details.services?.[0]?.service_id ? data.error_details.services?.[0]?.service_id : data.error_details.account_id ? data.error_details.account_id : data.error_details.namespace,
                                    }));
                                }, 5 * 1000);
                            }
                            // other type errors
                            else if (data.error_details.detail) {
                                loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                                set_alertOptions(alertOptions => ({
                                    status: true,
                                    type: "error",
                                    message: "Environment Creation failed.",
                                    description: formatErrors(data.error_details),
                                }));

                                // opacity=0 after 10sec
                                setTimeout(() => {
                                    set_alertOptions(alertOptions => ({
                                        status: false,
                                        type: "error",
                                        message: "Environment Creation failed.",
                                        description: formatErrors(data.error_details),
                                    }));
                                }, 5 * 1000);
                            }

                            // if main api fails due to some reason
                            else if (data.error_details && Array.isArray(data.error_details)) {
                                loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                                set_alertOptions(alertOptions => ({
                                    status: true,
                                    type: "error",
                                    message: "Environment Creation failed",
                                    description: "Please enter correct information for the services in order to proceed."
                                }));

                                // opacity=0 after 10sec
                                setTimeout(() => {
                                    set_alertOptions(alertOptions => ({
                                        status: false,
                                        type: "error",
                                        message: "Environment Creation failed",
                                        description: formatErrors(data.error_details),
                                    }));
                                }, 5 * 1000);                            
                            }

                            // for setting the errors in the forms 
                            else {
                                console.log("Error...");
                                loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                                let myErrors = {};
                                Object.keys(data.error_details?.services || {}).map((val) => {
                                    if (Object.keys(data.error_details.services[val]).length > 0) {
                                        myErrors = {
                                            ...myErrors,
                                            [val]: data.error_details.services[val]
                                        }
                                    }
                                });
                                console.log("MY errors: ", myErrors);
                                for (const val of allForms) {

                                    if (myErrors[val.serviceSelected]) {
                                        console.log("Val data source: ", val.form.getFieldWarning());

                                        val.form.setFields(
                                            Object.entries(myErrors[val.serviceSelected])
                                                // non_field_errors (and any key with no form field) would become
                                                // setFields({ name: undefined }) — they reach the user via the
                                                // alert below instead.
                                                .filter(([field]) => snakeToCamel[field])
                                                .map(([field, err]) => ({
                                                name: field === "name" ? (snakeToCamel[field] in val.form.getFieldsValue()) ? snakeToCamel[field] : "appName" : snakeToCamel[field],
                                                //warnings: val.form.getFieldWarning()[snakeToCamel[field]],
                                                errors: Array.isArray(err) ? err : flattenErrorDetails(err)
                                            }))
                                        );

                                        // console.log("Val.form.getFields", val.form.getFieldsError());
                                        // console.log("Val.form.getFields", val.form.getFieldsError());
                                        // // val.form.validateFields(['displayName']).then((res) => {
                                        // //     console.log("Validated", res);
                                        // // }).catch((err) => {
                                        // //     console.log("All errors info: ", err);
                                        // // });
                                        // // val.form.validateFields().then(() => {
                                        // //     console.log("Validated", val.form.getFieldsError());
                                        // // }).catch((error) => {

                                        // // });
                                        // //val.form.validateFields();
                                        // console.log("Val.form.getFields", val.form.getFieldsError());
                                        // console.log("Val.form.getFields", val.form.getFieldsError());
                                    }
                                }
                                console.log("All Forms: ", allForms[0]?.form.getFieldsError());
                                setFormErrors(myErrors);
                                // Say what the server actually said ("bitbucket: We do not support creation
                                // of kubernetes resource kind …"); the old generic line hid every detail,
                                // including YAML errors that belong to no form field.
                                const envErrorText = flattenErrorDetails(data.error_details).join(" ") || "Please enter correct information for the services in order to proceed.";
                                set_alertOptions(alertOptions => ({
                                    status: true,
                                    type: "error",
                                    message: "Environment Creation failed",
                                    description: envErrorText
                                }));

                                // opacity=0 after 10sec
                                setTimeout(() => {
                                    set_alertOptions(alertOptions => ({
                                        status: false,
                                        type: "error",
                                        message: "Environment Creation failed",
                                        description: envErrorText,
                                    }));
                                }, 5 * 1000);
                            }
                        };
                        api.post(process.env.REACT_APP_API_URI + '/api/project_env/env/create/', {
                            "cluster_unique_id": !cloneDep ? deploymentData.clusterId : cloneDeploymentData.clusterId,
                            "project": !cloneDep ? deploymentData.projectId : cloneDeploymentData.projectId,
                            "name": !cloneDep ? deploymentData.envName : cloneDeploymentData.envName,//appData.name,
                            "namespace": !cloneDep ? deploymentData.clusterNamespace : cloneDeploymentData.clusterNamespace,
                            "description": !cloneDep ? deploymentData.description === "" ? null : deploymentData.description : cloneDeploymentData.description === "" ? null : cloneDeploymentData.description,
                            "labels": deploymentLabels,
                            "parent_env_unique_id": cloneDep ? location.pathname.split('/').slice(-2)[0] : undefined,
                            "services": servicePayload
                        }).then(handleCreateEnvResponse).catch((err) => {
                            console.log("Error catch", err);
                            if (err?.response?.data?.error_details) {
                                handleCreateEnvResponse(err.response);
                                return;
                            }
                            set_alertOptions(alertOptions => ({
                                status: true,
                                type: "error",
                                message: "Environment Creation failed",
                                description: "Something went wrong. Please try again."
                            }));

                            // opacity=0 after 10sec
                            setTimeout(() => {
                                set_alertOptions(alertOptions => ({
                                    status: false,
                                    type: "error",
                                    message: "Environment Creation failed",
                                    description: "Something went wrong. Please try again."
                                }));
                            }, 5 * 1000);
                            loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                        });
                    }
                }
                else {
                    set_alertOptions(alertOptions => ({
                        status: true,
                        type: "error",
                        message: "Environment Creation failed.",
                        description: "There were some changes to the selected project. Please fill the information again."
                    }));

                    // opacity=0 after 10sec
                    setTimeout(() => {
                        set_alertOptions(alertOptions => ({
                            status: false,
                            type: "error",
                            message: "Environment Creation failed",
                            description: "There were some changes to the selected project. Please fill the information again."
                        }));
                    }, 5 * 1000);
                    navigate('/environments/create');
                }
            }
        }
        // add new service
        else {
            console.log("Else entered while checking");
            // Sidebar toggles update selectedProject.services[].isActive; application/redis/… objects may
            // still have serviceEnabled === true. Only validate/deploy services the user left enabled.
            const addDepServiceSidebarActive = (serviceName) => {
                const list = addUpdatedData?.selectedProject?.services;
                if (!Array.isArray(list) || list.length === 0) return true;
                const svc = list.find((s) => s.name === serviceName);
                if (!svc) return true;
                if (svc.isActive === undefined || svc.isActive === null) return true;
                return svc.isActive === true;
            };
            let allForms = [], allErrors = [];
            let myErrors = {};
            let checked = true;
            let applicationServicesList = Object.values(addUpdatedData.applicationData);
            let servicePayload = [];


            applicationServicesList.map((val) => {
                if (val.serviceEnabled === true && addDepServiceSidebarActive(val.basicDetails.serviceName)) {
                    const form = deploymentForms[val.basicDetails.serviceName];
                    allForms.push({
                        serviceSelected: val.basicDetails.serviceName,
                        form: form
                    });
                }
            });

            let yamls = await validateYamls(applicationServicesList);

            console.log("====My yamls====: ", yamls);

            let yamlError = false;

            yamls.forEach((val, idx) => {
                if (val && val.data && idx === val.idx) {
                    applicationServicesList[idx].deployments.yamlData = [...val.data];
                }
                else if (val && applicationServicesList[idx].serviceEnabled && addDepServiceSidebarActive(applicationServicesList[idx].basicDetails.serviceName)) {
                    myErrors = {
                        ...myErrors,
                        [val.serviceName]: "Your YAML code provided is invalid."
                    };
                    allErrors = [...allErrors, {
                        serviceSelected: val.serviceName,
                        error: "Your YAML code provided is invalid."
                    }];
                    yamlError = true;
                }
            });

            let dataSourcesMySqlList = Object.values(addUpdatedData.dataSourcesData);
            dataSourcesMySqlList.map((val) => {
                if (val.serviceEnabled && addDepServiceSidebarActive(val.serviceName)) {
                    console.log("val.displayName", val);
                    const form = deploymentForms[val.serviceName];
                    allForms.push({
                        serviceSelected: val.serviceName,
                        form: form
                    });
                }
            });

            console.log("Mysql values: ", allForms);

            let dataSourcesMongoList = Object.values(addUpdatedData.mongoDataSourcesData);
            dataSourcesMongoList.map((val) => {
                if (val.serviceEnabled && addDepServiceSidebarActive(val.serviceName)) {
                    console.log("val.displayName", val);
                    const form = deploymentForms[val.serviceName];
                    allForms.push({
                        serviceSelected: val.serviceName,
                        form: form
                    });
                }
            });

            let redisList = Object.values(addUpdatedData.redisData)
            console.log("Redis data: ",);
            redisList.map((val) => {
                if (val.serviceEnabled && addDepServiceSidebarActive(val.serviceName)) {
                    console.log("Val.data: ", val.cloudManaged.maintenanceWindowEnd);
                    const form = deploymentForms[val.serviceName];
                    allForms.push({
                        serviceSelected: val.serviceName,
                        form: form
                    });
                }
            });

            console.log("Entered here");

            let postgresList = Object.values(addUpdatedData.postgresDataSourcesData);
            postgresList.map((val) => {
                if (val.serviceEnabled && addDepServiceSidebarActive(val.serviceName)) {
                    const form = deploymentForms[val.serviceName];
                    console.log("Form: ", form);
                    allForms.push({
                        serviceSelected: val.serviceName,
                        form: form
                    });
                }
            });

            console.log("All forms: ", allForms);

            for (const val of allForms) {
                try {
                    let variable = await val.form.validateFields();
                    console.log("ending validation...", variable);
                }
                catch (error) {
                    console.log("Catch entered");
                    allErrors = [...allErrors, {
                        serviceSelected: val.serviceSelected,
                        error: error
                    }];
                    myErrors = {
                        ...myErrors,
                        [val.serviceSelected]: {
                            error: error
                        }
                    }
                }
            }

            console.log("My errors: ", myErrors);
            setFormErrors(myErrors);

            if (allErrors.length > 0) {
                if (yamlError) {
                    console.error('Validation failed: ', allErrors);
                    set_alertOptions(alertOptions => ({
                        status: true,
                        type: "error",
                        message: "Services couldn't be added.",
                        description: "Please enter a correct YAML file.",
                    }));

                    // opacity=0 after 10sec
                    setTimeout(() => {
                        set_alertOptions(alertOptions => ({
                            status: false,
                            type: "error",
                            message: "Services couldn't be added.",
                            description: "Please enter a correct YAML file.",
                        }));
                    }, 5 * 1000);
                }
                else {
                    console.error('Validation failed: ', allErrors);
                    set_alertOptions(alertOptions => ({
                        status: true,
                        type: "error",
                        message: "Services couldn't be added.",
                        description: "Please fill all the fields for the enabled services.",
                    }));

                    // opacity=0 after 10sec
                    setTimeout(() => {
                        set_alertOptions(alertOptions => ({
                            status: false,
                            type: "error",
                            message: "Services couldn't be added.",
                            description: "Please fill all the fields for the enabled services.",
                        }));
                    }, 5 * 1000);
                }
            }
            else {
                let myDepData = addUpdatedData?.deploymentData;
                if (myDepData && myDepData.projectName !== "" && myDepData.projectId !== 0) {
                    console.log("Validation succeeded");
                    //let servicePayload = [];
                    let applicationServicesList = Object.values(addUpdatedData.applicationData);
                    yamls.forEach((val, idx) => {
                        if (val && val.data && val.idx === idx) {
                            applicationServicesList[idx].deployments.yamlData = [...val.data];
                        }
                    });
                    console.log("Getting application services list: ", applicationServicesList);
                    let servicePayload = [];
                    let days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
                    applicationServicesList.map((val) => {
                        if (val.serviceEnabled === true && addDepServiceSidebarActive(val.basicDetails.serviceName)) {
                            let myConfigVol = [];
                            let mySecretVol = [];
                            let myStorageVol = [];
                            const envListAdd = val.environments || [];
                            let myEnvs = envListAdd.length > 0 ? (envListAdd.length === 1 ? (envListAdd[0].name === "" && envListAdd[0].value === "") ? [] : envListAdd : envListAdd) : envListAdd;
                            let labels = {};
                            normalizeDeploymentTagsForPayload(val.deployments?.tags).forEach((tagItem) => {
                                if (tagItem.name !== "" && tagItem.value !== "") {
                                    labels[tagItem.name] = tagItem.value;
                                }
                            });
                            (val.volumes || []).map((ele) => {
                                if (ele.volumeType === "Configmap") {
                                    let rawStr = ele.config.createMountPath.split('/')
                                    let tempFilename = rawStr.pop()  // removing filename from rawStr
                                    let tempMountPath = rawStr.join("/")   // join for mountPath

                                    console.log("tempFilename :", tempFilename)
                                    console.log("tempMountPath :", tempMountPath)

                                    myConfigVol.push({
                                        "name": ele.config.configName,//"test-cm",
                                        "filename": tempFilename,//"index.yaml",
                                        "mountPath": tempMountPath,//"/app",
                                        "data": ele.config.createConfig//"this the configmap data...."
                                    });
                                }
                                else if (ele.volumeType === "Secrets") {
                                    let rawStrSecrets = ele.secrets.createSecretsMountPath.split('/')
                                    let tempFilenameSecrets = rawStrSecrets.pop()  // removing filename from rawStrSecrets
                                    let tempMountPathSecrets = rawStrSecrets.join("/")   // join for mountPath

                                    mySecretVol.push({
                                        "name": ele.secrets.secretsName,//"test-secretqs",
                                        "filename": tempFilenameSecrets,//"index.yaml",
                                        "mountPath": tempMountPathSecrets,//"/app",
                                        "data": ele.secrets.createSecrets//"this the configmap data...."
                                    });
                                }
                                else if (ele.volumeType === "Storage") {
                                    myStorageVol.push({
                                        "name": ele.storage.storageDes.storageName,//"test-secrets",
                                        "size": ele.storage.storageDes.size !== "" ? ele.storage.storageDes.size : 0,//+ 'Gi' : "",//"10Gi",
                                        "mountPath": ele.storage.storageDes.path,//"/app",
                                        "storageClass": ele.storage.storageDes.type.toLowerCase() === "efs" ? 'efs-sc' : ele.storage.storageDes.type.toLowerCase(),//"gp2",
                                        "accessMode": "ReadWriteOnce"
                                    });
                                }
                            });

                            const matchedProjectTask = currentProjectDep?.tasks?.find((item) => item.serviceId === val.serviceSelectedId);
                            let secret_name = matchedProjectTask?.secret_name;
                            let accountDetails = matchedProjectTask?.accountDetails;
                            console.log("accountDetails :", accountDetails)
                            console.log("App details: ", val, (val.deployments.endpointType === "Cluster IP" || val.deployments.serviceType === "Existing Load Balancer") ? "ClusterIP" : !val.deployments.endpointType ? "" : "LoadBalancer");
                            servicePayload.push({
                                "name": val.basicDetails.appName,
                                "service_id": val.serviceSelectedId,//serviceSelected.id,
                                "cd_values_in_yaml": val.deployments.deploymentOption === "yaml",
                                "config": {
                                    "secret_details": accountDetails?.account_id ? {
                                        "secret_name": secret_name || "",
                                        "region": accountDetails?.region || "",
                                        "account_id": accountDetails?.account_id || "",
                                        "integration_id": accountDetails?.integration || "",
                                    } : null,
                                    //"container_registry_id": val.basicDetails.containerRegistryId.id,
                                    "docker_file": val.basicDetails.sourceType === "Container Registry" ? undefined : './' + val.basicDetails.dockerFilePath,
                                    "build_context": val.basicDetails.sourceType === "Container Registry" ? undefined : val.basicDetails.buildContext && val.basicDetails.buildContext !== "" ? "./" + val.basicDetails.buildContext : null,
                                    "app_source_type": val.basicDetails.sourceType === "Container Registry" ? 0 : 1,
                                    "container_registry_id": val.basicDetails.appSource === "PUBLIC" ? null : val.basicDetails.sourceType === "Container Registry" ? val.basicDetails.appSource?.id : val.basicDetails.containerRegistryId?.id ?? null,
                                    "container_registry_type": val.basicDetails.sourceType === "Container Registry" ? val.basicDetails.appSource === "PUBLIC" ? 0 : 1 : undefined,
                                    "repository_name": val.basicDetails.imageRepo,
                                    "image_tag": val.basicDetails.imageTag && val.basicDetails.imageTag !== "" ? val.basicDetails.imageTag : undefined,
                                    "architecture_type": val.basicDetails.architectureType,
                                    "port": val.deployments.deploymentOption === "visual" ? parseInt(val.deployments.port) : undefined,
                                    "create_storage": val.deployments.deploymentOption === "visual" ? myStorageVol.length > 0 ? "true" : "false" : undefined,
                                    "create_secret": val.deployments.deploymentOption === "visual" ? mySecretVol.length > 0 ? "true" : "false" : undefined,
                                    "ingress_enabled": val.deployments.deploymentOption === "visual" ? val.deployments.serviceType.includes("Existing Load Balancer") ? "true" : "false" : undefined,//val.deployments.customDomain === true ? "true" : "false",
                                    "ingress_https_enabled": val.deployments.deploymentOption === "visual" ? "true" : undefined,
                                    "ingress_host": val.deployments.deploymentOption === "visual" ? val.deployments.domainHostUrl === "" ? "" : val.deployments.domainHostUrl + '.' + val.deployments.dns : undefined,
                                    "zone_id": val.deployments.deploymentOption === "visual" ? (val.deployments.endpointType === "Cluster IP" ? null : val.deployments.hostedAccount): undefined,
                                    "dns_record_type": val.deployments.deploymentOption === "visual" ? val.deployments.recordType : undefined,
                                    "hosted_zone": val.deployments.deploymentOption === "visual" ? (val.deployments.endpointType === "Cluster IP" ? "" : val.deployments.dns): undefined,
                                    "ingress_path": val.deployments.deploymentOption === "visual" ? (val.deployments.endpointType === "Cluster IP" ? "/" : (val.deployments.gatewayHttps.gPath === "" ? "/" : val.deployments.gatewayHttps.gPath)) : undefined,
                                    "env_variables": myEnvs,
                                    "record_prefix": val.deployments.deploymentOption === "visual" ? val.deployments.serviceType.includes("Existing Load Balancer") ? val.deployments.domainHostUrl : "" : undefined,
                                    "service_type": val.deployments.deploymentOption === "visual" ? (val.deployments.endpointType === "Cluster IP" || val.deployments.serviceType.includes("Existing Load Balancer")) ? "ClusterIP" : !val.deployments.endpointType ? "" : "LoadBalancer" : undefined,//serviceSelected.type,
                                    "service_protocol": val.deployments.deploymentOption === "visual" ? "TCP" : undefined,
                                    "enable_plaintext_env": myEnvs.length > 0 ? "true" : "false",
                                    "enable_secret_env": myEnvs.length > 0 ? "true" : "false",
                                    "vcs_branch": val.basicDetails.branch === "" ? null : val.basicDetails.branch,
                                    "secretfile_enabled": val.deployments.deploymentOption === "visual" ? mySecretVol.length > 0 ? "true" : "false" : undefined,
                                    "create_configmap": val.deployments.deploymentOption === "visual" ? myConfigVol.length > 0 ? "true" : "false" : undefined,
                                    "cpu_request": val.deployments.deploymentOption === "visual" ? parseInt(val.deployments.resources.cpu.requestCpu) : undefined, //+ 'm',
                                    "cpu_limit": val.deployments.deploymentOption === "visual" ? parseInt(val.deployments.resources.cpu.limitCpu) : undefined, //+ 'm',
                                    "memory_request": val.deployments.deploymentOption === "visual" ? parseInt(val.deployments.resources.memory.requestMem) : undefined, //+ 'Mi',
                                    "memory_limit": val.deployments.deploymentOption === "visual" ? parseInt(val.deployments.resources.memory.limitMem) : undefined, //+ 'Mi',
                                    "target_cpu_utilization": val.deployments.deploymentOption === "visual" ? val.deployments.autoScaling.autoScalingData.autoScalingParameter.cpuAuto === "" ? 0 : parseInt(val.deployments.autoScaling.autoScalingData.autoScalingParameter.cpuAuto) : undefined, //+ '%',
                                    "target_memory_utilization": val.deployments.deploymentOption === "visual" ? val.deployments.autoScaling.autoScalingData.autoScalingParameter.memoryAuto === "" ? 0 : parseInt(val.deployments.autoScaling.autoScalingData.autoScalingParameter.memoryAuto) : undefined, //+ '%',
                                    "enable_autoscaling": val.deployments.deploymentOption === "visual"
                                        ? String(val.deployments.autoScaling?.enabled === true)
                                        : undefined,
                                    "maximum_replicas": val.deployments.deploymentOption === "visual" ? val.deployments.autoScaling.autoScalingData.maximumReplica || val.deployments.autoScaling.autoScalingData.maximumReplica === 0 ? val.deployments.autoScaling.autoScalingData.maximumReplica.toString() : "" : undefined,
                                    "minimum_replicas": val.deployments.deploymentOption === "visual" ? val.deployments.autoScaling.autoScalingData.minimumReplica || val.deployments.autoScaling.autoScalingData.minimumReplica === 0 ? val.deployments.autoScaling.autoScalingData.minimumReplica.toString() : "" : undefined,
                                    "config_files_config": val.deployments.deploymentOption === "visual" ? myConfigVol === 1 ? myConfigVol[0].name === "" ? [] : myConfigVol : myConfigVol : undefined,
                                    "storages_config": val.deployments.deploymentOption === "visual" ? myStorageVol.length === 1 ? myStorageVol[0].name === "" ? [] : myStorageVol : myStorageVol : undefined,
                                    "secret_files_config": val.deployments.deploymentOption === "visual" ? mySecretVol.length === 1 ? mySecretVol[0].name === "" ? [] : mySecretVol : mySecretVol : undefined,
                                    "labels": labels,
                                    "health_check_path": val.deployments.deploymentOption === "visual" ? val.deployments.healthCheckPath : undefined,
                                    "auto_trigger": String(val.basicDetails.enableAutoTrigger ?? false),
                                    "create_cron": val.deployments.deploymentOption === "visual" ? (val.deployments.taskSchedulers?.crons ? "true" : "false") : "false",
                                    "create_queue_worker": val.deployments.deploymentOption === "visual" ? (val.deployments.taskSchedulers?.queuesAndWorkers ? "true" : "false") : "false",
                                    "cron_command": val.deployments.deploymentOption === "visual" ? String(val.deployments.taskSchedulers?.entryPointCommandCrons ?? "") : "",
                                    "queue_worker_command": val.deployments.deploymentOption === "visual" ? String(val.deployments.taskSchedulers?.entryPointCommandQueues ?? "") : "",
                                    "pass_env_vars_at_build_time": String(
                                        Boolean(
                                            currentProjectDep?.tasks
                                                ?.find((x) => x.serviceId === val.serviceSelectedId)
                                                ?.envs?.some(
                                                    (x) =>
                                                        Array.isArray(x?.injectAt) &&
                                                        x.injectAt.includes("Build Time") &&
                                                        x.lastAction !== "Delete"
                                                )
                                        )
                                    ),
                                    "nodegroup_name": val.deployments.deploymentOption === "visual" ? val.deployments.nodeGroupName : undefined,
                                    "nodegroup_type": val.deployments.deploymentOption === "visual" ? val.deployments.nodeGroupType : undefined,
                                    "list_of_nodegroups": val.deployments.deploymentOption === "visual" ? (myDepData?.provisioner === "imported" ? [] : val.deployments.listOfNodegroups) : undefined,
                                    "ingress_alb_enabled": val.deployments.deploymentOption === "visual" ? val.deployments.serviceType === "Existing Load Balancer(ALB Ingress)" ? "true" : "false" : undefined,//deploymentData.ingressAlbEnabled.toString(),
                                    "acm_certificate": val.deployments.deploymentOption === "visual" && val.deployments.serviceType === "Existing Load Balancer(ALB Ingress)" ? acmCertificateForPayload(val.deployments.acmCertificate) : undefined,
                                    "yaml_content": val.deployments.deploymentOption === "yaml" ? val.deployments.yamlData : undefined,
                                    "yaml_kind": val.deployments.deploymentOption === "yaml" ? val.deployments.yamlType : undefined
                                },
                                //"context": val.basicDetails.context,
                                // "environments.plaintext.values": appData.envPlainVars,
                                // "environments.secret.values": appData.envSecretVars,
                                // "configmap_file_name": val.volumes[0].config.configFileName,
                                // "configmap_content": val.volumes[0].config.createConfig,
                                // "configmap_file_mountpath": val.volumes[0].config.createMountPath,
                                // "storage_size": val.volumes[0].storage.storageDes.size,
                                // "VolumemountPath": val.volumes[0].storage.storageDes.path,
                                // "storage_class": val.volumes[0].storage.storageDes.type //will be changed later
                            });
                        }
                    });

                    let dataSourcesMySqlAws = Object.values(addUpdatedData.dataSourcesData);
                    console.log("data sources: ", dataSourcesMySqlAws);

                    dataSourcesMySqlAws.map((val) => {
                        if (val.serviceEnabled && val.cloudManaged.serviceAws && addDepServiceSidebarActive(val.serviceName)) {
                            servicePayload.push({
                                "name": val.displayName,//"mysql",
                                "service_id": val.serviceSelectedId,//40,
                                "config": {
                                    "db_type": val.type === "AWS Managed MySQL" ? "aws-rds-managed-mysql" : "aws-aurora-managed-mysql",
                                    "mysql_instance_class": val.cloudManaged.instanceType,//"db.t3.micro" , 
                                    "Instance_family": val.cloudManaged.instanceFamily,
                                    "mysql_engine_version": val.version,
                                    "tags": val.cloudManaged.tags.length === 1 ? (val.cloudManaged.tags[0].name === "" || val.cloudManaged.tags[0].value === "") ? [] : val.cloudManaged.tags : val.cloudManaged.tags,
                                    "database_name": val.cloudManaged.databaseName,//"nodeapp",        
                                    "enable_multi_az": val.cloudManaged.dbType === "Single DB Instance" ? "false" : "true", //"false",
                                    "master_username": val.cloudManaged.username,//"abhi",
                                    "storage_size": parseInt(val.cloudManaged.storageSize),//"30",       
                                    "enable_public_access": val.cloudManaged.enablePublicAccess ? "true" : "false",//"true",
                                    "skip_final_snapshot": "true",
                                    "backup_window": (val.cloudManaged.backupTimingStart === "" || val.cloudManaged.backupTimingEnd === "") ? "" : val.cloudManaged.backupTimingStart + '-' + val.cloudManaged.backupTimingEnd,//"03:00-06:00",
                                    "backup_region": val.cloudManaged.backupRegion,
                                    "maintenance_window": (val.cloudManaged.maintenanceWindowStart === "" || val.cloudManaged.maintenanceWindowEnd === "") ? "" : val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowStart + '-' + val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowEnd,//"Mon:00:00-Mon:03:00",
                                    "enable_deletion_protection": val.cloudManaged.deletionProtection ? "true" : "false",//"false",
                                    "backup_retention_period": val.cloudManaged.backupRetention === "" ? "0" : val.cloudManaged.backupRetention,//"7",
                                    "enable_general_log": val.cloudManaged.enableGeneralLogs ? "true" : "false",//"true",
                                    "enable_slow_query_log": val.cloudManaged.enableSlowLogs ? "true" : "false",//"true",
                                    "create_random_password": val.cloudManaged.autoGenPassword ? "true" : "false",//"true"
                                    "master_password": val.cloudManaged.password,
                                    "log_retention_period": val.cloudManaged.logRetention,
                                    "private_subnet_ids": val.cloudManaged.subnetIds.privateSubnets,
                                    "public_subnet_ids": val.cloudManaged.subnetIds.publicSubnets
                                    //"enable_backup": val.cloudManaged.enableBackup ? "true" : 'false'
                                }
                            });
                        }
                    });

                    let dataSourcesMySqlSelf = Object.values(addUpdatedData.dataSourcesData);
                    console.log("Mysql self: ", dataSourcesMySqlSelf);
                    dataSourcesMySqlSelf.map((val) => {
                        if (val.serviceEnabled && val.selfManaged.serviceSelf && addDepServiceSidebarActive(val.serviceName)) {
                            console.log("Executes");
                            const labelsMysql = buildSelfManagedLabelsPayload(val.selfManaged.labels);
                            // Check multiple possible fields for storageSize
                            const rawStorageSize = val.selfManaged.storageSize || val.selfManaged.masterStorageSize || "";
                            // Only parse if we have a valid non-empty value
                            const storageSizeInt = rawStorageSize && rawStorageSize !== "" && !isNaN(parseInt(rawStorageSize))
                                ? parseInt(rawStorageSize) 
                                : undefined;
                            // Ensure dbType (replica count) is a valid integer, defaulting to 0 if empty
                            const replicaCount = val.selfManaged.dbType !== undefined && val.selfManaged.dbType !== null && val.selfManaged.dbType !== "" 
                                ? parseInt(val.selfManaged.dbType) 
                                : 0;
                            
                            servicePayload.push({
                                "name": val.displayName,
                                "service_id": val.serviceSelectedId,//3,
                                "config": {
                                    "db_type": "self-managed-mysql",
                                    //"architecture": val.selfManaged.dbType,//"standalone",
                                    "storage_class_name": myDepData?.provisioner !== "atmosly" ? "" : val.selfManaged.storageType.toLowerCase() === "efs" ? "efs-sc" : val.selfManaged.storageType.toLowerCase(),//"gp2",
                                    "username": val.selfManaged.username,//"admin",
                                    ...(storageSizeInt !== undefined ? {
                                        "master_db_volume_size": storageSizeInt,
                                        "secondary_db_volume_size": storageSizeInt,
                                    } : {}),
                                    "secondary_db_replica_count": replicaCount,//"1",
                                    "backup_enabled": val.selfManaged.enableBackup ? "true" : "false",//"false",
                                    "bucket_uri": "",
                                    "enable_cross_region_backup": val.selfManaged.enableCrossRegionBackup.toString(),
                                    "bucket_region": val.selfManaged.crossRegionBackup,//"",
                                    //"backup_cron": "* * * * *",
                                    "restore_db": "false",
                                    "exporter_enable": val.selfManaged.enableMetrics ? "true" : "false",//"true",
                                    "app_version": val.version,
                                    "custom_credentials_enabled": !val.selfManaged.autoGenPassword ? "true" : "false",//"true",
                                    "store_password_to_secret_manager": "false",
                                    "custom_user_password": val.selfManaged.password,//"password",
                                    "backup_frequency": val.selfManaged.backupFrequency.toLowerCase(),//"hourly",
                                    "backup_interval": val.selfManaged.backupFrequency === "Weekly" ? days.indexOf(val.selfManaged.backupDay) : val.selfManaged.backupFrequency !== "Hourly" ? 1 : val.selfManaged.backupInterval,//3,
                                    "backup_time": val.selfManaged.backupTiming,
                                    "database_name": val.selfManaged.databaseName,
                                    "restore_file_name": "",
                                    "list_of_nodegroups": myDepData?.provisioner === "imported" ? [] : val.selfManaged.listOfNodegroups,
                                    "restore_db": val.selfManaged.enableRestore ? "true" : "false",
                                    "primary_cpu_limit": val.selfManaged.limitCpuPrimary,
                                    "primary_memory_limit": val.selfManaged.limitMemPrimary,
                                    "primary_cpu_request": val.selfManaged.requestCpuPrimary,
                                    "primary_memory_request": val.selfManaged.requestMemPrimary,
                                    "secondary_cpu_limit": val.selfManaged.limitCpuSecondary,
                                    "secondary_memory_limit": val.selfManaged.limitMemSecondary,
                                    "secondary_cpu_request": val.selfManaged.requestCpuSecondary,
                                    "secondary_memory_request": val.selfManaged.requestMemSecondary,
                                    "labels": labelsMysql
                                }
                            });
                        }
                    });

                    let dataSourcesMongoSelf = Object.values(addUpdatedData.mongoDataSourcesData);
                    console.log("Mysql self: ", dataSourcesMongoSelf);
                    dataSourcesMongoSelf.map((val) => {
                        if (val.serviceEnabled && val.selfManaged.serviceSelf && addDepServiceSidebarActive(val.serviceName)) {
                            const labelsMongo = buildSelfManagedLabelsPayload(val.selfManaged.labels);
                            // Check multiple possible fields for storageSize
                            const rawStorageSize = val.selfManaged.storageSize || "";
                            // Only parse if we have a valid non-empty value
                            const storageSizeInt = rawStorageSize && rawStorageSize !== "" && !isNaN(parseInt(rawStorageSize))
                                ? parseInt(rawStorageSize) 
                                : undefined;
                            // Ensure dbType (replica count) is a valid integer, defaulting to 0 if empty
                            const replicaCount = val.selfManaged.dbType !== undefined && val.selfManaged.dbType !== null && val.selfManaged.dbType !== "" 
                                ? parseInt(val.selfManaged.dbType) 
                                : 0;
                            
                            servicePayload.push({
                                "name": val.displayName,//"dskldss",
                                "service_id": val.serviceSelectedId,//6,
                                "type": "self-managed-mongodb",
                                "config": {
                                    "db_type": "self-managed-mongodb",
                                    //"architecture": val.selfManaged.dbType.toLowerCase(),//"standalone",
                                    "storage_class_name": myDepData?.provisioner !== "atmosly" ? "" : val.selfManaged.storageType.toLowerCase() === "efs" ? "efs-sc" : val.selfManaged.storageType.toLowerCase(),                            //"username": val.selfManaged.username,//"admin",
                                    ...(storageSizeInt !== undefined ? {
                                        "volume_size": storageSizeInt,
                                    } : {}), //+ 'Gi',//"10Gi",
                                    //"secondary_db_volume_size": val.selfManaged.storageSize === "" ? "" : val.selfManaged.storageSize + 'Gi',//"10Gi",
                                    "replica_count": replicaCount,
                                    //"replica_count": "1",
                                    "backup_enabled": val.selfManaged.enableBackup ? "true" : "false",//"false", //backup_enable
                                    "bucket_uri": "",
                                    "enable_cross_region_backup": val.selfManaged.enableCrossRegionBackup.toString(),
                                    "bucket_region": val.selfManaged.crossRegionBackup,//"", //bucket_region
                                    "restore_db": "false",
                                    "exporter_enable": val.selfManaged.enableMetrics ? "true" : "false",//"true",
                                    //"mongodb_version": "",//"5.0.8-debian-10-r9", //later
                                    "app_version": val.version,
                                    "custom_credentials_enabled": !val.selfManaged.autoGenPassword ? "true" : "false",//"true",
                                    "store_password_to_secret_manager": "false",
                                    //"root_password": val.selfManaged.password,//"password",
                                    "backup_frequency": val.selfManaged.backupFrequency.toLowerCase(),//"hourly",
                                    "backup_interval": val.selfManaged.backupFrequency === "Weekly" ? days.indexOf(val.selfManaged.backupDay) : val.selfManaged.backupFrequency !== "Hourly" ? 1 : val.selfManaged.backupInterval,//3,
                                    "backup_time": val.selfManaged.backupTiming,
                                    "custom_database_name": val.selfManaged.databaseName,
                                    "custom_database_password": val.selfManaged.password,
                                    "custom_database_username": val.selfManaged.username,//"abhi"
                                    "list_of_nodegroups": myDepData?.provisioner === "imported" ? [] : val.selfManaged.listOfNodegroups,
                                    "restore_db": val.selfManaged.enableRestore ? "true" : "false",
                                    "primary_cpu_limit": val.selfManaged.limitCpuPrimary,
                                    "primary_memory_limit": val.selfManaged.limitMemPrimary,
                                    "primary_cpu_request": val.selfManaged.requestCpuPrimary,
                                    "primary_memory_request": val.selfManaged.requestMemPrimary,
                                    "labels": labelsMongo
                                }
                            });
                        }
                    });

                    let redisCloud = Object.values(addUpdatedData.redisData);
                    redisCloud.map((val) => {
                        if (val.serviceEnabled && val.cloudManaged.serviceCloud && addDepServiceSidebarActive(val.serviceName)) {
                            servicePayload.push({
                                "name": val.displayName,
                                "service_id": val.serviceSelectedId,
                                //"type": "elasticache-redis",
                                "config": {
                                    "db_type": "elasticache-redis",
                                    "node_type": val.cloudManaged.instanceType,
                                    "Instance_family": val.cloudManaged.instanceFamily,
                                    //"family": "redis6.x",//will append the version to redis
                                    "num_cache_nodes": val.cloudManaged.noOfReplica,//"",
                                    "multi_az_enabled": val.cloudManaged.enableMultiAz ? "true" : "false",
                                    "automatic_failover_enabled": val.cloudManaged.enableAutomaticFailover ? "true" : "false",
                                    "at_rest_encryption_enabled": val.cloudManaged.enableRestEncryption ? "true" : "false",
                                    // "snapshot_retention_limit": val.cloudManaged.backupRetention === "" ? "0" : val.cloudManaged.backupRetention,//val.cloudManaged.backupRetention ? "true" : "false",
                                    "transit_encryption_enabled": val.cloudManaged.enableTransitEncryption ? "true" : "false",
                                    // "snapshot_window": (val.cloudManaged.backupTimingStart === "" || val.cloudManaged.backupTimingEnd === "") ? "" : val.cloudManaged.backupTimingStart + '-' + val.cloudManaged.backupTimingEnd,
                                    "maintenance_window": (val.cloudManaged.maintenanceWindowStart === "" || val.cloudManaged.maintenanceWindowEnd === "") ? "" : val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowStart + '-' + val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowEnd,
                                    "engine_version": val.version,
                                    "private_subnet_ids": val.cloudManaged.subnetIds.privateSubnets,
                                    "public_subnet_ids": val.cloudManaged.subnetIds.publicSubnets
                                }
                            });
                        }
                    });

                    let redisSelf = Object.values(addUpdatedData.redisData);
                    redisSelf.map((val) => {
                        if (val.serviceEnabled && val.selfManaged.serviceSelf && addDepServiceSidebarActive(val.serviceName)) {
                            const labelsRedis = buildSelfManagedLabelsPayload(val.selfManaged.labels);
                            // Check multiple possible fields for storageSize
                            const rawStorageSize = val.selfManaged.storageSize || "";
                            // Only parse if we have a valid non-empty value
                            const storageSizeInt = rawStorageSize && rawStorageSize !== "" && !isNaN(parseInt(rawStorageSize))
                                ? parseInt(rawStorageSize) 
                                : undefined;
                            // Ensure noOfReplica (replica count) is a valid integer, defaulting to 0 if empty
                            const replicaCount = val.selfManaged.noOfReplica !== undefined && val.selfManaged.noOfReplica !== null && val.selfManaged.noOfReplica !== "" 
                                ? parseInt(val.selfManaged.noOfReplica) 
                                : 0;
                            
                            servicePayload.push({
                                "name": val.displayName,//"mks3",
                                "service_id": val.serviceSelectedId,//5,
                                //"type": "self-managed-redis",
                                "config": {
                                    "db_type": "self-managed-redis",
                                    //"architecture": "replication", //will add later
                                    //"replicacount": "1", //will add later
                                    "storage_class_name": myDepData?.provisioner !== "atmosly" ? "" : val.selfManaged.storageType.toLowerCase() === "efs" ? "efs-sc" : val.selfManaged.storageType.toLowerCase(),//"gp2",
                                    //"enable_metrics": val.selfManaged.enableMetrics ? "true" : "false",//"true",
                                    ...(storageSizeInt !== undefined ? {
                                        "slave_volume_size": storageSizeInt,
                                        "master_volume_size": storageSizeInt,
                                    } : {}), //+ "Gi",//"10Gi",
                                    "slave_replica_count": replicaCount,//"2", //will add later
                                    //"monitoring_enabled": "true", //will be added later
                                    "app_version": val.version,//"6.2.7-debian-11-r11", //version list update required
                                    "store_password_to_secret_manager": "false",
                                    "custom_credentials_enabled": val.selfManaged.authEnabled ? !val.selfManaged.autoGenPassword ? "true" : "false" : undefined,//"true",
                                    "password": val.selfManaged.authEnabled ? val.selfManaged.password : undefined,
                                    "grafana_monitoring_enabled": val.selfManaged.enableMetrics ? "true" : "false",
                                    "list_of_nodegroups": myDepData?.provisioner === "imported" ? [] : val.selfManaged.listOfNodegroups,
                                    "auth_enabled": val.selfManaged.authEnabled.toString(),
                                    "primary_cpu_limit": val.selfManaged.limitCpuPrimary,
                                    "primary_memory_limit": val.selfManaged.limitMemPrimary,
                                    "primary_cpu_request": val.selfManaged.requestCpuPrimary,
                                    "primary_memory_request": val.selfManaged.requestMemPrimary,
                                    "secondary_cpu_limit": val.selfManaged.limitCpuSecondary,
                                    "secondary_memory_limit": val.selfManaged.limitMemSecondary,
                                    "secondary_cpu_request": val.selfManaged.requestCpuSecondary,
                                    "secondary_memory_request": val.selfManaged.requestMemSecondary,
                                    "labels": labelsRedis
                                }
                            });
                        }
                    });

                    let postgresAws = Object.values(addUpdatedData.postgresDataSourcesData);
                    postgresAws.map((val) => {
                        if (val.serviceEnabled && val.cloudManaged.serviceAws && addDepServiceSidebarActive(val.serviceName)) {
                            servicePayload.push({
                                "name": val.displayName,//"mysql",
                                "service_id": val.serviceSelectedId,//40,
                                "config": {
                                    "db_type": val.type === "AWS Managed Postgres" ? "aws-rds-managed-postgres" : "aws-aurora-managed-postgres",
                                    "instance_type": val.cloudManaged.instanceType,//"db.t3.micro" , 
                                    "Instance_family": val.cloudManaged.instanceFamily,
                                    "engine_version": val.version,
                                    "tags": val.cloudManaged.tags.length === 1 ? (val.cloudManaged.tags[0].name === "" || val.cloudManaged.tags[0].value === "") ? [] : val.cloudManaged.tags : val.cloudManaged.tags,
                                    "database_name": val.cloudManaged.databaseName,//"nodeapp",        
                                    "enable_multi_az": val.cloudManaged.dbType === "Single DB Instance" ? "false" : "true", //"false",
                                    "master_username": val.cloudManaged.username,//"abhi",
                                    "storage_size": parseInt(val.cloudManaged.storageSize),//"30",       
                                    "enable_public_access": val.cloudManaged.enablePublicAccess ? "true" : "false",//"true",
                                    "skip_final_snapshot": "true",
                                    "backup_window": val.type === "AWS Aurora Managed Postgres" ? (val.cloudManaged.backupTimingStart === "" || val.cloudManaged.backupTimingEnd === "") ? "" : val.cloudManaged.backupTimingStart + '-' + val.cloudManaged.backupTimingEnd :
                                        (val.cloudManaged.backupTimingStart === "" || val.cloudManaged.backupTimingEnd === "") ? "" : val.cloudManaged.backupTimingStart + '-' + val.cloudManaged.backupTimingEnd,//"03:00-06:00",
                                    "maintenance_window": (val.cloudManaged.maintenanceWindowStart === "" || val.cloudManaged.maintenanceWindowEnd === "") ? "" : val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowStart + '-' + val.cloudManaged.maintenanceWindowDay.substring(0, 3).toLowerCase() + ':' + val.cloudManaged.maintenanceWindowEnd,//"Mon:00:00-Mon:03:00",
                                    "enable_deletion_protection": val.cloudManaged.deletionProtection ? "true" : "false",//"false",
                                    "backup_retention_period": val.cloudManaged.backupRetention === "" ? "0" : val.cloudManaged.backupRetention,//"7",
                                    "enable_general_log": val.cloudManaged.enableGeneralLogs ? "true" : "false",//"true",
                                    "enable_slow_query_log": val.cloudManaged.enableSlowLogs ? "true" : "false",//"true",
                                    "create_random_password": val.cloudManaged.autoGenPassword ? "true" : "false",//"true"
                                    "master_password": val.cloudManaged.password,
                                    "log_retention_period": val.cloudManaged.logRetention,
                                    "private_subnet_ids": val.cloudManaged.subnetIds.privateSubnets,
                                    "public_subnet_ids": val.cloudManaged.subnetIds.publicSubnets
                                    //"enable_backup": val.cloudManaged.enableBackup ? "true" : 'false'
                                }
                            });
                        }
                    });

                    let postgresSelf = Object.values(addUpdatedData.postgresDataSourcesData);
                    postgresSelf.map((val) => {
                        if (val.serviceEnabled && val.selfManaged.serviceSelf && addDepServiceSidebarActive(val.serviceName)) {
                            const labelsPostgres = buildSelfManagedLabelsPayload(val.selfManaged.labels);
                            // Check multiple possible fields for storageSize
                            const rawStorageSize = val.selfManaged.storageSize || "";
                            // Only parse if we have a valid non-empty value
                            const storageSizeInt = rawStorageSize && rawStorageSize !== "" && !isNaN(parseInt(rawStorageSize))
                                ? parseInt(rawStorageSize) 
                                : undefined;
                            // Ensure dbType (replica count) is a valid integer, defaulting to 0 if empty
                            const replicaCount = val.selfManaged.dbType !== undefined && val.selfManaged.dbType !== null && val.selfManaged.dbType !== "" 
                                ? parseInt(val.selfManaged.dbType) 
                                : 0;
                            
                            servicePayload.push({
                                "name": val.displayName,//"mks2",
                                "service_id": val.serviceSelectedId,//69,
                                "config": {
                                    "db_type": "self-managed-postgresql",
                                    "postgresql_exporter_enabled": val.selfManaged.enableExporter ? "true" : "false",//"false",
                                    "replicacount": replicaCount,//"1",
                                    "storage_class_name": myDepData?.provisioner !== "atmosly" ? "" : val.selfManaged.storageType.toLowerCase() === "efs" ? "efs-sc" : val.selfManaged.storageType.toLowerCase(),//"gp2",
                                    "custom_credentials_enabled": !val.selfManaged.autoGenPassword ? "true" : "false",//"true",
                                    "postgresql_password": val.selfManaged.password, //"",
                                    "app_version": val.version,//"16.0.0",
                                    "database_name": val.selfManaged.databaseName,//"atmosly",
                                    ...(storageSizeInt !== undefined ? {
                                        "storage_size": storageSizeInt,
                                    } : {}),//"10Gi"
                                    "postgresql_username": val.selfManaged.username,
                                    "backup_enabled": val.selfManaged.enableBackup ? "true" : "false",
                                    "backup_frequency": val.selfManaged.backupFrequency.toLowerCase(),
                                    "backup_interval": val.selfManaged.backupFrequency === "Weekly" ? days.indexOf(val.selfManaged.backupDay) : val.selfManaged.backupFrequency !== "Hourly" ? 1 : val.selfManaged.backupInterval,
                                    "backup_time": val.selfManaged.backupTiming,
                                    "enable_cross_region_backup": val.selfManaged.enableCrossRegionBackup.toString(),
                                    "bucket_region": val.selfManaged.crossRegionBackup,
                                    "list_of_nodegroups": myDepData?.provisioner === "imported" ? [] : val.selfManaged.listOfNodegroups,
                                    "restore_db": val.selfManaged.enableRestore ? "true" : "false",
                                    "primary_cpu_limit": val.selfManaged.limitCpuPrimary,
                                    "primary_memory_limit": val.selfManaged.limitMemPrimary,
                                    "primary_cpu_request": val.selfManaged.requestCpuPrimary,
                                    "primary_memory_request": val.selfManaged.requestMemPrimary,
                                    "secondary_cpu_limit": val.selfManaged.limitCpuSecondary,
                                    "secondary_memory_limit": val.selfManaged.limitMemSecondary,
                                    "secondary_cpu_request": val.selfManaged.requestCpuSecondary,
                                    "secondary_memory_request": val.selfManaged.requestMemSecondary,
                                    "labels": labelsPostgres
                                }
                            });
                        }
                    });

                    console.log("Payload", servicePayload);

                    if (servicePayload.length === 0) {
                        set_alertOptions(alertOptions => ({
                            status: true,
                            type: "error",
                            message: "Environment Creation failed.",
                            description: "Please enable at least one service in order to proceed.",
                        }));

                        // opacity=0 after 10sec
                        setTimeout(() => {
                            set_alertOptions(alertOptions => ({
                                status: false,
                                type: "error",
                                message: "Environment Creation failed.",
                                description: "Please enable at least one service in order to proceed.",
                            }));
                        }, 5 * 1000);
                    }
                    else {
                        loadDispatch({ loader: "createDep", status: "loading", nestLevel: 0 });
                        // loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                        // console.log("Entered else");
                        let envId = location.pathname.split('/').slice(-1)[0];
                        // Same as env create: validation failures arrive as HTTP 400 (a rejection),
                        // so the body handler must be reachable from .catch as well.
                        const handleAddServiceResponse = (res) => {
                            let data = res.data;
                            console.log("Enters");
                            if (data.status) {
                                console.log("Entered");
                                loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                                setAddUpdatedData({});
                                setAddDep(false);
                                setUpdateDep(false);
                                setCloneDep(false);
                                localStorage.setItem('isClone', JSON.stringify(false));
                                localStorage.setItem('updateDep', JSON.stringify(false));
                                localStorage.setItem('addDep', JSON.stringify(false));
                                localStorage.setItem('stopFetch', JSON.stringify(false));
                                localStorage.removeItem('addUpdatedData');
                                navigate(`/environments/environment/${envId}/env-overview`, {
                                    state: {
                                        from: "/environments/services"
                                    }
                                });
                                // if (!cloneDep) {
                                //     setApplicationData({});
                                //     setDataSourcesData({});
                                //     setMongoDataSourcesData({});
                                //     setRedisData({});
                                //     setServicesEnabled({});
                                //     setSelectedProject({});
                                //     setPostgresDataSourcesData({});
                                //     setContainerRegistries({});
                                //     setCurrentProjectDep({});
                                //     setDeploymentData({});
                                //     setServiceCategories({});
                                //     RemoveEnvironmentFromLocal();
                                // }
                                // else {
                                //     handleDeploymentLogout();
                                //     RemoveCloneEnvironmentFromLocal();
                                // }
                            }
                            else if (data.error_details && (data.error_details.cluster_unique_id || data.error_details.name || data.error_details.namespace || data.error_details.account_id || data.error_details.services?.[0]?.service_id)) {
                                loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });

                                set_alertOptions(alertOptions => ({
                                    status: true,
                                    type: "error",
                                    message: "Add new service failed.",
                                    description: data.error_details.cluster_unique_id ? data.error_details.cluster_unique_id : data.error_details.name ? data.error_details.name : data.error_details.services?.[0]?.service_id ? data.error_details.services?.[0]?.service_id : data.error_details.account_id ? data.error_details.account_id : data.error_details.namespace,
                                }));

                                // opacity=0 after 10sec
                                setTimeout(() => {
                                    set_alertOptions(alertOptions => ({
                                        status: false,
                                        type: "error",
                                        message: "Add new service failed.",
                                        description: data.error_details.cluster_unique_id ? data.error_details.cluster_unique_id : data.error_details.name ? data.error_details.name : data.error_details.services?.[0]?.service_id ? data.error_details.services?.[0]?.service_id : data.error_details.account_id ? data.error_details.account_id : data.error_details.namespace,
                                    }));
                                }, 5 * 1000);
                            }
                            else if (data.error_details.detail) {
                                loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                                set_alertOptions(alertOptions => ({
                                    status: true,
                                    type: "error",
                                    message: "Add new service failed.",
                                    description: formatErrors(data.error_details),
                                }));

                                // opacity=0 after 10sec
                                setTimeout(() => {
                                    set_alertOptions(alertOptions => ({
                                        status: false,
                                        type: "error",
                                        message: "Add new service failed.",
                                        description: formatErrors(data.error_details),
                                    }));
                                }, 5 * 1000);
                            }
                            else if (data.error_details && Array.isArray(data.error_details)) {
                                loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                                set_alertOptions(alertOptions => ({
                                    status: true,
                                    type: "error",
                                    message: "Add new service failed.",
                                    description: "Please enter correct information for the services in order to proceed."
                                }));

                                // opacity=0 after 10sec
                                setTimeout(() => {
                                    set_alertOptions(alertOptions => ({
                                        status: false,
                                        type: "error",
                                        message: "Add new service failed.",
                                        description: formatErrors(data.error_details),
                                    }));
                                }, 5 * 1000);                                    
                            }
                            else {
                                console.log("Error...");
                                loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                                let myErrors = {};
                                Object.keys(data.error_details?.services || {}).map((val) => {
                                    if (Object.keys(data.error_details.services[val]).length > 0) {
                                        myErrors = {
                                            ...myErrors,
                                            [val]: data.error_details.services[val]
                                        }
                                    }
                                });
                                console.log("MY errors: ", myErrors);
                                for (const val of allForms) {

                                    if (myErrors[val.serviceSelected]) {
                                        console.log("Val data source: ", val.form.getFieldWarning());

                                        val.form.setFields(
                                            Object.entries(myErrors[val.serviceSelected])
                                                // non_field_errors (and any key with no form field) would become
                                                // setFields({ name: undefined }) — they reach the user via the
                                                // alert below instead.
                                                .filter(([field]) => snakeToCamel[field])
                                                .map(([field, err]) => ({
                                                name: field === "name" ? (snakeToCamel[field] in val.form.getFieldsValue()) ? snakeToCamel[field] : "appName" : snakeToCamel[field],
                                                //warnings: val.form.getFieldWarning()[snakeToCamel[field]],
                                                errors: Array.isArray(err) ? err : flattenErrorDetails(err)
                                            }))
                                        );

                                        // console.log("Val.form.getFields", val.form.getFieldsError());
                                        // console.log("Val.form.getFields", val.form.getFieldsError());
                                        // // val.form.validateFields(['displayName']).then((res) => {
                                        // //     console.log("Validated", res);
                                        // // }).catch((err) => {
                                        // //     console.log("All errors info: ", err);
                                        // // });
                                        // // val.form.validateFields().then(() => {
                                        // //     console.log("Validated", val.form.getFieldsError());
                                        // // }).catch((error) => {

                                        // // });
                                        // //val.form.validateFields();
                                        // console.log("Val.form.getFields", val.form.getFieldsError());
                                        // console.log("Val.form.getFields", val.form.getFieldsError());
                                    }
                                }
                                console.log("All Forms: ", allForms[0]?.form.getFieldsError());
                                setFormErrors(myErrors);
                                const addServiceErrorText = flattenErrorDetails(data.error_details).join(" ") || "Please enter correct information for the services in order to proceed.";
                                set_alertOptions(alertOptions => ({
                                    status: true,
                                    type: "error",
                                    message: "Add new service failed",
                                    description: addServiceErrorText
                                }));

                                // opacity=0 after 10sec
                                setTimeout(() => {
                                    set_alertOptions(alertOptions => ({
                                        status: false,
                                        type: "error",
                                        message: "Add new service failed",
                                        description: addServiceErrorText,
                                    }));
                                }, 5 * 1000);
                            }
                        };
                        api.post(process.env.REACT_APP_API_URI + `/api/project_env/update/${envId}`, servicePayload).then(handleAddServiceResponse).catch((err) => {
                            console.log("Error catch", err);
                            if (err?.response?.data?.error_details) {
                                handleAddServiceResponse(err.response);
                                return;
                            }
                            set_alertOptions(alertOptions => ({
                                status: true,
                                type: "error",
                                message: "Add new service failed",
                                description: "Something went wrong. Please try again."
                            }));

                            // opacity=0 after 10sec
                            setTimeout(() => {
                                set_alertOptions(alertOptions => ({
                                    status: false,
                                    type: "error",
                                    message: "Add new service failed",
                                    description: "Something went wrong. Please try again."
                                }));
                            }, 5 * 1000);
                            loadDispatch({ loader: "createDep", status: "loaded", nestLevel: 0 });
                        });
                    }
                }
                else {
                    set_alertOptions(alertOptions => ({
                        status: true,
                        type: "error",
                        message: "Add new service failed.",
                        description: "There were some changes to the selected project. Please fill the information again."
                    }));

                    // opacity=0 after 10sec
                    setTimeout(() => {
                        set_alertOptions(alertOptions => ({
                            status: false,
                            type: "error",
                            message: "Add new service failed",
                            description: "There were some changes to the selected project. Please fill the information again."
                        }));
                    }, 5 * 1000);
                    //navigate('/environments/create');
                }
            }
        }
    }

    const handleDeploymentLogout = () => {
        if (!cloneDep) {
            setApplicationData({});
            setDataSourcesData({});
            setMongoDataSourcesData({});
            setRedisData({});
            setServicesEnabled({});
            setSelectedProject({});
            setContainerRegistries({});
            setCurrentProjectDep({});
            setDeploymentData({ ...initialDeploymentData });
            setServiceCategories({});
            setPostgresDataSourcesData({});
            setOpenDebugIssues(false);
            // setEnvHealthIssuesCounts(false);
        }
        else {
            setCloneApplicationData({});
            setCloneDataSourcesData({});
            setCloneMongoDataSourcesData({});
            setCloneRedisData({});
            setCloneServicesEnabled({});
            setCloneSelectedProject({});
            setCloneContainerRegistries({});
            setCurrentProjectDep({});
            setCloneDeploymentData({ ...initialDeploymentData });
            setCloneServiceCategories([]);
            setClonePostgresDataSourcesData({});
            setImportantEnabled(false);
            setNextBtn(false);
            setPrevBtn(false);
            setImportantFields({});
            setSelectedEnv({});
            setCurrentProjectDep({});
            setOpenDebugIssues(false);
            // setEnvHealthIssuesCounts(false);
        }
    }

    const [envHealthIssuesCounts, setEnvHealthIssuesCounts] = useState(false);
    const [individualSummaryFetchingStates, setIndividualSummaryFetchingStates] = useState({});
    const [openDebugIssues, setOpenDebugIssues] = useState(false);
    const [deploymentData, setDeploymentData] = useState(initialDeploymentData);
    const [cloneDeploymentData, setCloneDeploymentData] = useState(initialDeploymentData);
    const [selectedProject, setSelectedProject] = useState({});
    const [cloneSelectedProject, setCloneSelectedProject] = useState({});
    const [updateSelectedProject, setUpdateSelectedProject] = useState({});
    const [applicationData, setApplicationData] = useState({});
    const [applicationPayload, setApplicationPayload] = useState({});
    const [cloneApplicationData, setCloneApplicationData] = useState({});
    const [cloneApplicationPayload, setCloneApplicationPayload] = useState({});
    const [containerRegistries, setContainerRegistries] = useState([]);
    const [cloneContainerRegistries, setCloneContainerRegistries] = useState([]);
    const [serviceCategories, setServiceCategories] = useState([]);
    const [cloneServiceCategories, setCloneServiceCategories] = useState([]);
    const [cloneDep, setCloneDep] = useState(false);
    const [importantEnabled, setImportantEnabled] = useState(false);
    const [currentProjectDep, setCurrentProjectDep] = useState(JSON.parse(JSON.stringify(initialProject)));

    const [dataSourcesData, setDataSourcesData] = useState({});
    const [cloneDataSourcesData, setCloneDataSourcesData] = useState({});

    const [servicesEnabled, setServicesEnabled] = useState({});
    const [cloneServicesEnabled, setCloneServicesEnabled] = useState({});
    const [mongoDataSourcesData, setMongoDataSourcesData] = useState({});
    const [cloneMongoDataSourcesData, setCloneMongoDataSourcesData] = useState({});
    const [redisData, setRedisData] = useState({});
    const [cloneRedisData, setCloneRedisData] = useState({});
    const [postgresDataSourcesData, setPostgresDataSourcesData] = useState({});
    const [clonePostgresDataSourcesData, setClonePostgresDataSourcesData] = useState({});
    const [selectedEnv, setSelectedEnv] = useState({});
    const [allAppsSelected, setAllAppsSelected] = useState([]);
    const [deploymentForms, setDeploymentForms] = useState({}); //please note that I'm using this state for sharing any form data to the topbar(not specific to env section)
    const [formErrors, setFormErrors] = useState({});
    const [releaseStatus, setReleaseStatus] = useState("");
    const { set_alertOptions } = useContext(AlertContext);
    const { loadDispatch } = useContext(AppContext);
    const [importantFields, setImportantFields] = useState({});
    const [nextBtn, setNextBtn] = useState(false);
    const [prevBtn, setPrevBtn] = useState(false);
    const [myKeySelected, setMyKeySelected] = useState({
        index: 0,
        serviceName: ""
    });
    const [clonePages, setClonePages] = useState({
        envData: false,
        serviceData: false
    });
    let location = useLocation();
    const [subTab, setSubTab] = useState("");
    const [selectedService, setSelectedService] = useState(""); //for env overview select.
    //const [canFetchEnvs, setCanFetchEnvs] = useState(true);

    const [updatedData, setUpdatedData] = useState({});
    const [updateDep, setUpdateDep] = useState(false);
    const [editServiceDataChanged, setEditServiceDataChanged] = useState(false);

    const [addUpdatedData, setAddUpdatedData] = useState();
    const [addDep, setAddDep] = useState(false);
    const [callAddMethod, setCallAddMethod] = useState(false);
    const [appServiceData, setAppServiceData] = useState({});
    const [dataSourceServiceData, setDataSourceServiceData] = useState({});
    const [serviceList, setServiceList] = useState([]);
    const [selectedTab, setSelectedTab] = useState("Environment Overview");
    const [loadService, setLoadService] = useState({
        application: false,
        dataSource: false,
        queues: false
    });

    const [initialEnvDeploymentData, setInitialEnvDeploymentData] = useState({
        id: "",
        envName: "",
        project: "",
        namespace: "",
        uniqueId: "",
        projectName: "",
        cloudAccountNo: "",
        cloudLogo: "",
        cloudAccount: "",
        clusterRegion: "",
        clusterId: "",
        kubernetesVersion: "",
        serviceDetails: [],
        totalServices: "",
        healthyServices: "",
        unhealthyServices: "",
        totalReleases: "",
        healthyReleases: "",
        unhealthyReleases: "",
        clusterName: "",
        availabilityZones: [],
        dns: "",
        hostedAccount: null,
        recordType: null,
        clusterIntegrationId: "",
        projectId: "",
        tags: [],
        provisioner: "",
        vpc_id: ""
    });

    const [toggleApi, setToggleApi] = useState(false); // can be used for re-triggering api calls via useEffect

    const [envDeploymentData, setEnvDeploymentData] = useState(JSON.parse(JSON.stringify(initialEnvDeploymentData)));
    const [warningsObj, setWarningsObj] = useState({});
    const [currentImportantField, setCurrentImportantField] = useState("");
    // const [prevImportantField, setPrevImportantField] = useState("");
    const [currentForm, setCurrentForm] = useState();
    const [serviceDisabled, setServiceDisabled] = useState(false);
    const [isRetrigger, setIsRetrigger] = useState(false);
    // Set by the open app's page: { serviceId, disabledReason, open(kinds) } so the
    // header's Service menu can offer "Restart Service"; null when no app page is open.
    const [serviceRestart, setServiceRestart] = useState(null);
    const [mapIdsWithAppName, setMapIdsWithAppName] = useState({});
    const [isServiceDeleted, setIsServiceDeleted] = useState(false);
    const [prerequisite, setPrerequisite] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [triggerSaveFromTopbar, setTriggerSaveFromTopbar] = useState(false);
    const [sampleEnvData, setSampleEnvData] = useState({ ...initialDeploymentData });
    const [nextImportantField, setNextImportantField] = useState("");

    const [generateImpField, setGenerateImpField] = useState(true); //set to true by default


    // useEffect(() => {
    //     if(importantEnabled) {
    //         setInitialWarnings({...warningsObj});
    //     }
    // }, [importantEnabled]);

    // useEffect(() => {
    //     console.log("Warnings: ", warningsObj, currentImportantField, prevImportantField, nextImportantField);
    //     let allForms = deploymentForms;
    //     if (importantEnabled) {
    //         if (Object.keys(warningsObj).length > 0 && warningsObj[currentForm]) {
    //             if (importantEnabled) {
    //                 console.log("entering check....2", JSON.parse(JSON.stringify(warningsObj)));
    //                 let arr = Object.entries(warningsObj[currentForm]).map(([field, err]) => ({
    //                     name: field,//snakeToCamel[field],
    //                     warnings: err
    //                 }));
    //                 arr = arr.filter(x => allForms[currentForm].getFieldInstance(x.name)?.input?.disabled === true ? false : x); //this will filter out the elements if they get manually disabled by the user while they are editing the form
    //                 console.log("Filtering current, prev and next: ", currentImportantField, prevImportantField, nextImportantField);
    //                 if (currentImportantField !== "" || prevImportantField !== "" || nextImportantField !== "") {
    //                     arr = arr.filter(x => x.name === currentImportantField || x.name === prevImportantField || x.name === nextImportantField);
    //                 }
    //                 console.log("Setting the arr: ", arr);
    //                 allForms[currentForm].setFields(arr);

    //                 console.log("All Forms val:", allForms[currentForm].getFieldsError());
    //                 //}
    //             }
    //         }
    //     }
    //     else {
    //         if (Object.keys(warningsObj).length > 0 && warningsObj[currentForm]) {
    //             let arr = Object.entries(warningsObj[currentForm]).map(([field, err]) => ({
    //                 name: field,//snakeToCamel[field],
    //                 warnings: []
    //             }));
    //             allForms[currentForm].setFields(arr);
    //             setWarningsObj({});
    //         }
    //     }
    // }, [importantEnabled, deploymentForms, warningsObj]);

    useEffect(() => {
        console.log("I'm inside deployment context", warningsObj);
        //localStorage.setItem('canFetchEnvs', JSON.stringify(true));
        if (!updateDep) {
            const project = localStorage.getItem("ProjectDep");
            if (JSON.parse(project)) {
                setCurrentProjectDep({ ...JSON.parse(project) });
            }
        }
        else {
            const project = JSON.parse(localStorage.getItem("updatedData")).currentProjectDep;
            if (JSON.parse(project)) {
                setUpdatedData({
                    ...updatedData,
                    currentProjectDep: project
                });
            }
        }
    }, []);

    //For application

    useEffect(() => {
        if (updateDep || addDep) {
            // const projData = localStorage.getItem("updateSelectedProjectData");
            // if (JSON.parse(projData)) {
            //     setUpdateSelectedProject({ ...JSON.parse(projData) });
            // }
            if (updateDep) {
                const updateData = localStorage.getItem('updatedData');
                const sampleUpdateData = localStorage.getItem('sampleEnvData');
                if (JSON.parse(updateData)) {
                    setUpdatedData({ ...JSON.parse(updateData) });
                }
                if (JSON.parse(sampleUpdateData)) {
                    setSampleEnvData({ ...JSON.parse(sampleUpdateData) });
                }
            }
            else {
                const addData = localStorage.getItem('addUpdatedData');
                if (JSON.parse(addData)) {
                    setAddUpdatedData({ ...JSON.parse(addData) });
                }
            }
        }
        else if (!cloneDep) {
            const depData = localStorage.getItem("deploymentData");
            if (JSON.parse(depData)) {
                setDeploymentData({ ...JSON.parse(depData) });
            }
            const myData = localStorage.getItem("app-data");
            if (JSON.parse(myData)) {
                console.log("Setting the data to app", myData);
                setApplicationData({ ...JSON.parse(myData) });
            }
            const appPayload = localStorage.getItem("appPayload");
            if (JSON.parse(appPayload)) {
                setApplicationPayload({ ...JSON.parse(appPayload) });
            }
            const projData = localStorage.getItem("selectedProjectData");
            if (JSON.parse(projData)) {
                setSelectedProject({ ...JSON.parse(projData) });
            }
            const containerReg = localStorage.getItem("container-registries");
            if (JSON.parse(containerReg)) {
                setContainerRegistries([...JSON.parse(containerReg)]);
            }
            const serviceCat = localStorage.getItem("service-categories");
            if (JSON.parse(serviceCat)) {
                setServiceCategories([...JSON.parse(serviceCat)]);
            }
            const serviceEnabled = localStorage.getItem("servicesEnabled");
            if (JSON.parse(serviceEnabled)) {
                setServicesEnabled({ ...JSON.parse(serviceEnabled) });
            }
        }

        else {
            const cloneDepData = localStorage.getItem("cloneDeploymentData");
            if (JSON.parse(cloneDepData)) {
                setCloneDeploymentData({ ...JSON.parse(cloneDepData) });
            }
            const cloneAppData = localStorage.getItem("cloneAppData");
            if (JSON.parse(cloneAppData)) {
                setCloneApplicationData({ ...JSON.parse(cloneAppData) });
            }
            const cloneAppPayload = localStorage.getItem("cloneAppPayload");
            if (JSON.parse(cloneAppPayload)) {
                setCloneApplicationPayload({ ...JSON.parse(cloneAppPayload) });
            }
            const cloneProjData = localStorage.getItem("cloneSelectedProjectData");
            if (JSON.parse(cloneProjData)) {
                setCloneSelectedProject({ ...JSON.parse(cloneProjData) });
            }
            const cloneContainerReg = localStorage.getItem("cloneContainerRegistries");
            if (JSON.parse(cloneContainerReg)) {
                setCloneContainerRegistries([...JSON.parse(cloneContainerReg)]);
            }
            const cloneServiceCat = localStorage.getItem("cloneServiceCategories");
            const parsed = JSON.parse(cloneServiceCat);
            if (parsed) {
                setCloneServiceCategories(Array.isArray(parsed) ? [...parsed] : []);
            }
            const cloneServiceEnabled = localStorage.getItem("cloneServicesEnabled");
            if (JSON.parse(cloneServiceEnabled)) {
                setCloneServicesEnabled({ ...JSON.parse(cloneServiceEnabled) });
            }
            const impFields = localStorage.getItem("importantFields");
            if (JSON.parse(impFields)) {
                setImportantFields({ ...JSON.parse(impFields) });
            }
        }

        const isUpdate = localStorage.getItem("updateDep");
        if (JSON.parse(isUpdate)) {
            console.log("Setting the value of updateDep", isUpdate);
            setUpdateDep(JSON.parse(isUpdate));
        }

        const isAdd = localStorage.getItem("addDep");
        if (JSON.parse(isAdd)) {
            setAddDep(JSON.parse(isAdd));
        }
        const isClone = localStorage.getItem("isClone");
        if (JSON.parse(isClone)) {
            setCloneDep(JSON.parse(isClone));
        }
        const clonedPages = localStorage.getItem("clonedPages");
        if (JSON.parse(clonedPages)) {
            setClonePages({ ...JSON.parse(clonedPages) });
        }
    }, [cloneDep, updateDep, addDep]);

    //For data sources

    useEffect(() => {
        if (!cloneDep) {
            const dataSources = localStorage.getItem("dataSourcesData");
            if (JSON.parse(dataSources)) {
                setDataSourcesData({ ...JSON.parse(dataSources) });
            }
            const mongoDataSources = localStorage.getItem("mongoDataSourcesData");
            if (JSON.parse(mongoDataSources)) {
                setMongoDataSourcesData({ ...JSON.parse(mongoDataSources) });
            }
            const postgresDataSources = localStorage.getItem("postgresDataSourcesData");
            if (JSON.parse(postgresDataSources)) {
                setPostgresDataSourcesData({ ...JSON.parse(postgresDataSources) });
            }
        }
        else {
            const cloneDataSources = localStorage.getItem("cloneDataSourcesData");
            if (JSON.parse(cloneDataSources)) {
                setCloneDataSourcesData({ ...JSON.parse(cloneDataSources) });
            }
            const cloneMongoDataSources = localStorage.getItem("cloneMongoDataSourcesData");
            if (JSON.parse(cloneMongoDataSources)) {
                setCloneMongoDataSourcesData({ ...JSON.parse(cloneMongoDataSources) });
            }
            const clonePostgresDataSources = localStorage.getItem("clonePostgresDataSourcesData");
            if (JSON.parse(clonePostgresDataSources)) {
                setClonePostgresDataSourcesData({ ...JSON.parse(clonePostgresDataSources) });
            }
            const cloneRedisDataSources = localStorage.getItem("cloneRedisData");
            if (JSON.parse(cloneRedisDataSources)) {
                setCloneRedisData({ ...JSON.parse(cloneRedisDataSources) });
            }
        }
    }, [cloneDep]);

    useEffect(() => {
        if (!cloneDep) {
            const redisService = localStorage.getItem("redisData");
            if (JSON.parse(redisService)) {
                setRedisData({ ...JSON.parse(redisService) });
            }
        }
        else {
            const cloneRedisService = localStorage.getItem("cloneRedisData");
            if (JSON.parse(cloneRedisService)) {
                setRedisData({ ...JSON.parse(cloneRedisService) });
            }
        }
    }, [cloneDep]);

    useEffect(() => {
        if (cloneDep) {
            const selectEnv = localStorage.getItem('selectedEnv');
            if (JSON.parse(selectEnv)) {
                setSelectedEnv({ ...JSON.parse(selectEnv) });
            }
        }
    }, [cloneDep]);

    const sentrySnapshot = useMemo(() => ({
        selectedEnv,
        selectedProject,
        releaseStatus,
        deploymentData,
        applicationData,
        dataSourcesData,
        mongoDataSourcesData,
        postgresDataSourcesData,
        redisData,
        envHealthIssuesCounts,
        openDebugIssues,
        importantFields,
        serviceDisabled,
    }), [
        selectedEnv,
        selectedProject,
        releaseStatus,
        deploymentData,
        applicationData,
        dataSourcesData,
        mongoDataSourcesData,
        postgresDataSourcesData,
        redisData,
        envHealthIssuesCounts,
        openDebugIssues,
        importantFields,
        serviceDisabled,
    ]);
    useSentryContext("deploymentContext", sentrySnapshot);

    return (
        <DeploymentContext.Provider value={{
            currentForm, setCurrentForm,
            warningsObj, setWarningsObj,
            currentImportantField, setCurrentImportantField,
            initialEnvDeploymentData, setInitialEnvDeploymentData,
            envDeploymentData, setEnvDeploymentData,
            loadService, setLoadService,
            selectedTab, setSelectedTab,
            serviceList, setServiceList,
            dataSourceServiceData, setDataSourceServiceData,
            appServiceData, setAppServiceData,
            callAddMethod, setCallAddMethod,
            addDep, setAddDep,
            addUpdatedData, setAddUpdatedData,
            updateDep, setUpdateDep,
            updatedData, setUpdatedData,
            selectedService, setSelectedService,
            subTab, setSubTab,
            clonePages, setClonePages,
            myKeySelected, setMyKeySelected,
            impFieldConvert,
            nextBtn, setNextBtn,
            importantFields, setImportantFields,
            releaseStatus, setReleaseStatus,
            handleDeploymentLogout,
            deploymentData, setDeploymentData,
            applicationData, setApplicationData,
            applicationPayload, setApplicationPayload,
            selectedProject, setSelectedProject,
            updateSelectedProject, setUpdateSelectedProject,
            containerRegistries, setContainerRegistries,
            serviceCategories, setServiceCategories,
            cloneServiceCategories, setCloneServiceCategories,
            cloneDeploymentData, setCloneDeploymentData,
            cloneSelectedProject, setCloneSelectedProject,
            cloneApplicationData, setCloneApplicationData,
            cloneApplicationPayload, setCloneApplicationPayload,
            cloneContainerRegistries, setCloneContainerRegistries,
            dataSourcesData, setDataSourcesData,
            cloneDataSourcesData, setCloneDataSourcesData,
            cloneDep, setCloneDep,
            initialApplicationData,
            initialDataSourcesData,
            servicesEnabled, setServicesEnabled,
            cloneServicesEnabled, setCloneServicesEnabled,
            handleSubmit,
            selectedEnv, setSelectedEnv,
            initialMongoDataSourcesData,
            mongoDataSourcesData, setMongoDataSourcesData,
            cloneMongoDataSourcesData, setCloneMongoDataSourcesData,
            currentProjectDep, setCurrentProjectDep,
            redisData, setRedisData,
            cloneRedisData, setCloneRedisData,
            initialRedisData,
            postgresDataSourcesData, setPostgresDataSourcesData,
            clonePostgresDataSourcesData, setClonePostgresDataSourcesData,
            initialPostgresData,
            allAppsSelected, setAllAppsSelected,
            deploymentForms, setDeploymentForms,
            formErrors, setFormErrors,
            importantEnabled, setImportantEnabled,
            serviceDisabled, setServiceDisabled,
            isRetrigger, setIsRetrigger,
            serviceRestart, setServiceRestart,
            mapIdsWithAppName, setMapIdsWithAppName,
            isServiceDeleted, setIsServiceDeleted,
            prerequisite, setPrerequisite,
            isSubmitted, setIsSubmitted,
            triggerSaveFromTopbar, setTriggerSaveFromTopbar,
            sampleEnvData, setSampleEnvData,
            prevBtn, setPrevBtn,
            nextImportantField, setNextImportantField,
            validateYamls, warnings,
            generateImpField, setGenerateImpField,
            snakeToCamel, toggleApi, setToggleApi,
            openDebugIssues, setOpenDebugIssues,
            envHealthIssuesCounts, setEnvHealthIssuesCounts,
            individualSummaryFetchingStates, setIndividualSummaryFetchingStates,
            editServiceDataChanged, setEditServiceDataChanged
        }}>
            {children}
        </DeploymentContext.Provider>
    );
}

export { DeploymentContext, DeploymentProvider };